import { Fragment, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Alert, Badge, Button, Card, Container, Spinner, Modal } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../appointment/appointment.css';
import { buildWhatsappUrl } from '../../../../utils/FormatUtils';
import {
    getCompanyInfo,
    getCollaboratorPortalDashboard,
    getCollaboratorPortalAgenda,
    getCollaboratorPortalAgendaBlocks,
    createCollaboratorPortalAgendaBlock,
    cancelCollaboratorPortalAgendaBlock,
    getCollaboratorPortalScoreHistory,
    getCollaboratorPortalRevenueHistory,
    getCollaboratorPortalPushPublicKey,
    subscribeCollaboratorPortalPush,
    unsubscribeCollaboratorPortalPush,
    getEmployeePhotoUrl
} from '../../../../services/PublicBookingService';
import usePortalInstall from './usePortalInstall';
import PdfExportButton from '../../../components/Common/Report/PdfExportButton';
import PdfIcon from '../../../components/Icon/PdfIcon';
import AddToHomeScreenHint from './AddToHomeScreenHint';
import CollaboratorQuickBooking from './CollaboratorQuickBooking';
import PortalCustomerHistory from './PortalCustomerHistory';
import { formatQuantity } from './portalFormat';
import ClubCrownIcon from '../../../components/Icon/ClubCrownIcon';
import '../booking/PublicBooking.css';
import './PublicEmployeeDashboard.css';
import { ThemeContext } from '../../../../context/ThemeContext';
import colmeiaLogo from '../../../../images/logo.png';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
});

const monthReferenceFormatter = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric'
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const decodeVapidKey = (value) => {
    const padded = `${value}${'='.repeat((4 - (value.length % 4)) % 4)}`;
    const raw = window.atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(raw, (character) => character.charCodeAt(0));
};

// Quantidade sai inteira na maioria dos casos; a fração existe para produto vendido por peso
// e só aparece quando realmente há.
// Janela mínima (min) para considerar um intervalo entre atendimentos como "livre" e exibi-lo.
const FREE_GAP_THRESHOLD_MIN = 15;

const getAppointmentEnd = (appointment) => {
    if (appointment?.endTime) return new Date(appointment.endTime);
    const start = new Date(appointment.scheduledDate);
    const durationMin = appointment?.duration || 30;
    return new Date(start.getTime() + durationMin * 60000);
};

const formatGapDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours && mins) return `${hours}h${String(mins).padStart(2, '0')}`;
    if (hours) return `${hours}h`;
    return `${mins}min`;
};

const formatHourMinute = (date) => date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const timeToMinutes = (value) => {
    if (!value) return null;
    const parts = String(value).split(':').map(Number);
    if (!Number.isInteger(parts[0]) || !Number.isInteger(parts[1])) return null;
    return (parts[0] * 60) + parts[1];
};

const getWorkingHoursForDay = (workingHours, date) => {
    const dayName = JS_DAY_TO_ENUM[date.getDay()];
    return (workingHours || []).filter((item) => item.dayOfWeek === dayName || item.dayOfWeek?.value === dayName);
};

const portalCalendarLocalizer = momentLocalizer(moment);

const formatPortalCalendarDay = (date) => {
    const formatted = moment(date).format('ddd DD/MM');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const getPortalCalendarStatusClass = (status) => {
    switch (status) {
        case 'SCHEDULED': return 'event-scheduled';
        case 'CONFIRMED': return 'event-info';
        case 'IN_PROGRESS': return 'event-primary';
        case 'COMPLETED': return 'event-success';
        case 'CANCELED': return 'event-danger';
        case 'NO_SHOW': return 'event-dark';
        default: return 'event-secondary';
    }
};

const getPortalCalendarEventStyle = (event) => {
    const appointment = event.resource;
    const style = {};
    // Bloqueio não é atendimento: sai em cinza listrado, sem cor de status, para não ser
    // confundido com cliente marcado nem com horário livre.
    if (appointment?.agendaBlock) {
        return { className: 'collab-calendar-event--blocked', style };
    }
    if (appointment?.clubMember) {
        style.background = 'linear-gradient(135deg, #f7d070 0%, #fdf6cf 40%, #dfb244 70%, #c39627 100%)';
        style.backgroundColor = '#dfb244';
        style.borderColor = '#c39627';
        style.color = '#3b2501';
    }
    if (appointment?.status === 'COMPLETED') {
        style.background = '#9ca3af';
        style.backgroundColor = '#9ca3af';
        style.borderColor = '#9ca3af';
        style.color = '#1f2937';
    }
    if (appointment?.status === 'NO_SHOW') {
        style.backgroundColor = '#dc3545';
        style.borderColor = '#b02a37';
        style.color = '#ffffff';
    }
    if (appointment?.status === 'CANCELED') {
        style.backgroundColor = '#f59e0b';
        style.borderColor = '#d97706';
        style.color = '#451a03';
    }
    const statusClass = getPortalCalendarStatusClass(appointment?.status);
    const clubClass = appointment?.clubMember ? ' collab-calendar-event--club' : '';
    return { className: `${statusClass}${clubClass}`, style };
};

const PortalCalendarEvent = ({ event }) => {
    const appointment = event.resource;
    if (appointment?.agendaBlock) {
        return (
            <div className="collab-calendar-event collab-calendar-event--block-body">
                <div className="collab-calendar-event__time">
                    <i className="fa-solid fa-lock"></i>
                    <span>{formatHourMinute(event.start)} - {formatHourMinute(event.end)}</span>
                </div>
                <div className="collab-calendar-event__customer">
                    <span>{appointment.agendaBlock.reason || 'Bloqueado'}</span>
                </div>
            </div>
        );
    }
    return (
        <div className="collab-calendar-event">
            <div className="collab-calendar-event__time">
                <i className="fa-regular fa-clock"></i>
                <span>{formatHourMinute(event.start)} - {formatHourMinute(event.end)}</span>
            </div>
            <div className="collab-calendar-event__customer">
                <i className="fa-regular fa-user"></i>
                <span>{appointment.customerName || 'Cliente não informado'}</span>
                {appointment.clubMember && <ClubCrownIcon className="collab-calendar-event__club" aria-label="Cliente Clube" />}
            </div>
            <div className="collab-calendar-event__service">
                <i className="fa fa-list-ul"></i>
                <span>{formatServices(appointment)}</span>
            </div>
        </div>
    );
};

// Um atendimento pode ter vários serviços. Usa a lista (serviceNames) quando vier; senão, o serviço único.
const formatServices = (appointment) =>
    appointment?.serviceNames && appointment.serviceNames.length > 0
        ? appointment.serviceNames.join(' + ')
        : (appointment?.serviceName || 'Serviço não informado');

// getDay() (0=Dom..6=Sáb) → nome do enum DayOfWeek usado no working-hours do backend.
const JS_DAY_TO_ENUM = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

// Converte "HH:mm[:ss]" + dia (YYYY-MM-DD) num Date local.
const timeOnDate = (dateKey, timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':').map(Number);
    const date = new Date(dateKey + 'T00:00:00');
    date.setHours(h || 0, m || 0, 0, 0);
    return date;
};

// Monta a grade do dia: cartões de atendimento + janelas livres + intervalo, ordenados por horário.
// Usa o expediente da filial (quando disponível) para mostrar livre antes do 1º e depois do último
// atendimento; sem expediente, cai pra só os buracos entre atendimentos.
const buildDayTimeline = (appointments, dateKey, workingHoursList) => {
    const sorted = [...appointments].sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
    if (!sorted.length) return [];

    const branchId = sorted.find((a) => a.branchId != null)?.branchId;
    const enumDay = JS_DAY_TO_ENUM[new Date(dateKey + 'T12:00:00').getDay()];
    const list = workingHoursList || [];
    // Casa pelo branchId + dia; se o atendimento não traz branchId, cai pra qualquer expediente do dia.
    const workingHours = list.find((w) => w.branchId === branchId && w.dayOfWeek === enumDay)
        || (branchId == null ? list.find((w) => w.dayOfWeek === enumDay) : undefined);

    let windowStart = new Date(sorted[0].scheduledDate);
    let windowEnd = getAppointmentEnd(sorted[sorted.length - 1]);
    let breakBlock = null;

    if (workingHours && workingHours.isOpen && workingHours.openTime && workingHours.closeTime) {
        const open = timeOnDate(dateKey, workingHours.openTime);
        const close = timeOnDate(dateKey, workingHours.closeTime);
        if (open && open < windowStart) windowStart = open;
        if (close && close > windowEnd) windowEnd = close;
        if (workingHours.breakStartTime && workingHours.breakEndTime) {
            const breakStart = timeOnDate(dateKey, workingHours.breakStartTime);
            const breakEnd = timeOnDate(dateKey, workingHours.breakEndTime);
            if (breakStart && breakEnd && breakEnd > breakStart) breakBlock = { start: breakStart, end: breakEnd };
        }
    }

    // Intervalos ocupados (atendimentos + almoço) para subtrair da janela do dia.
    const occupied = sorted.map((a) => ({ start: new Date(a.scheduledDate), end: getAppointmentEnd(a) }));
    if (breakBlock) occupied.push(breakBlock);
    occupied.sort((a, b) => a.start - b.start);

    const freeWindows = [];
    let cursor = windowStart;
    occupied.forEach(({ start, end }) => {
        if (start > cursor && (start - cursor) / 60000 >= FREE_GAP_THRESHOLD_MIN) {
            freeWindows.push({ start: new Date(cursor), end: new Date(start) });
        }
        if (end > cursor) cursor = end;
    });
    if (windowEnd > cursor && (windowEnd - cursor) / 60000 >= FREE_GAP_THRESHOLD_MIN) {
        freeWindows.push({ start: new Date(cursor), end: new Date(windowEnd) });
    }

    const entries = [
        ...sorted.map((a) => ({ kind: 'appointment', start: new Date(a.scheduledDate), appointment: a })),
        ...freeWindows.map((f) => ({ kind: 'free', start: f.start, end: f.end })),
    ];
    if (breakBlock) entries.push({ kind: 'break', start: breakBlock.start, end: breakBlock.end });
    entries.sort((a, b) => a.start - b.start);
    return entries;
};

const formatTimeRange = (startDateStr, endDateStr) => {
    if (!startDateStr) return '';
    const start = new Date(startDateStr);
    const startHour = String(start.getHours()).padStart(2, '0');
    const startMin = String(start.getMinutes()).padStart(2, '0');
    
    if (endDateStr) {
        const end = new Date(endDateStr);
        const endHour = String(end.getHours()).padStart(2, '0');
        const endMin = String(end.getMinutes()).padStart(2, '0');
        return `${startHour}:${startMin} - ${endHour}:${endMin}`;
    }
    
    return `${startHour}:${startMin}`;
};

const formatDateOnly = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const formatted = new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long'
    }).format(date);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const formatGroupHeader = (dateKey) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
    
    if (dateKey === todayStr) {
        return 'Hoje';
    } else if (dateKey === tomorrowStr) {
        return 'Amanhã';
    }
    
    const date = new Date(dateKey + 'T12:00:00');
    const formatted = new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
    }).format(date);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const formatMonthReference = (value) => {
    if (!value || !/^\d{4}-\d{2}$/.test(value)) {
        return value || '--';
    }

    const [year, month] = value.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    const formattedValue = monthReferenceFormatter.format(date);
    return formattedValue.charAt(0).toUpperCase() + formattedValue.slice(1);
};

const getCurrentMonthReference = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthReferenceOptions = () => {
    const currentMonth = new Date();
    currentMonth.setDate(1);
    return Array.from({ length: 6 }, (_, index) => {
        const month = new Date(currentMonth);
        month.setMonth(currentMonth.getMonth() - index);
        return `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
    });
};

const shiftMonthReference = (value, amount) => {
    const [year, month] = value.split('-').map(Number);
    const nextMonth = new Date(year, month - 1 + amount, 1);
    return `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`;
};

// Teto de dias do período livre do extrato. Espelha CollaboratorPortalService.MAX_RANGE_DAYS:
// validar aqui evita que o colaborador só descubra o limite depois do erro da API.
const MAX_EXTRACT_RANGE_DAYS = 31;

const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Período sugerido ao abrir o seletor: do dia 1º do mês escolhido até hoje (ou até o último dia,
 * quando o mês já fechou). É o recorte que o extrato já mostra por padrão.
 */
const getMonthDefaultRange = (monthReference) => {
    const [year, month] = monthReference.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
        start: toDateInputValue(firstDay),
        end: toDateInputValue(today < lastDay ? today : lastDay)
    };
};

const countRangeDays = (range) => {
    // Datas de calendário não devem variar com transições de horário de verão do aparelho.
    const start = new Date(`${range.start}T00:00:00Z`);
    const end = new Date(`${range.end}T00:00:00Z`);
    return Math.floor((end - start) / 86400000) + 1;
};

/** Mensagem de erro do período, ou null quando o range é válido. */
const validateExtractRange = (range) => {
    if (!range?.start || !range?.end) return 'Informe a data inicial e a data final.';
    const days = countRangeDays(range);
    if (days <= 0) return 'A data final não pode ser anterior à inicial.';
    if (days > MAX_EXTRACT_RANGE_DAYS) return `O período não pode passar de ${MAX_EXTRACT_RANGE_DAYS} dias.`;
    return null;
};

const formatRangeLabel = (range) => {
    const format = (value) => new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
    return `${format(range.start)} até ${format(range.end)}`;
};

/** Identidade do recorte carregado, usada como chave de cache do extrato. */
const buildHistoryKey = (range) => `${range?.start}..${range?.end}`;

const getWeekStart = (date) => {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    const day = result.getDay();
    result.setDate(result.getDate() - (day === 0 ? 6 : day - 1));
    return result;
};

const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'CONFIRMED':
            return 'Confirmado';
        case 'IN_PROGRESS':
            return 'Em andamento';
        case 'COMPLETED':
            return 'Concluído';
        case 'SCHEDULED':
            return 'Agendado';
        default:
            return status || 'Agendado';
    }
};

const getStatusVariant = (status) => {
    switch (status) {
        case 'SCHEDULED':
            return 'scheduled';
        case 'CONFIRMED':
            return 'info';
        case 'IN_PROGRESS':
            return 'primary';
        case 'COMPLETED':
            return 'secondary';
        case 'CANCELED':
            return 'warning';
        case 'NO_SHOW':
            return 'danger';
        default:
            return 'secondary';
    }
};

const playNotificationSound = (type, audioCtx) => {
    try {
        if (!audioCtx) return;
        if (audioCtx.state === 'suspended') audioCtx.resume();
        
        if (type === 'new') {
            const playChime = (time, pitch) => {
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();
                osc.connect(gain);
                gain.connect(audioCtx.destination);
                
                osc.type = 'sine';
                osc.frequency.setValueAtTime(pitch, time);
                
                gain.gain.setValueAtTime(0.15, time);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.3);
                
                osc.start(time);
                osc.stop(time + 0.3);
            };
            
            const now = audioCtx.currentTime;
            playChime(now, 523.25); // C5
            playChime(now + 0.15, 659.25); // E5
        } else if (type === 'cancel') {
            const playTone = (time, pitch) => {
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();
                osc.connect(gain);
                gain.connect(audioCtx.destination);
                
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(pitch, time);
                
                gain.gain.setValueAtTime(0.12, time);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.25);
                
                osc.start(time);
                osc.stop(time + 0.25);
            };
            
            const now = audioCtx.currentTime;
            playTone(now, 392.00); // G4
            playTone(now + 0.12, 349.23); // F4
            playTone(now + 0.24, 293.66); // D4
        }
    } catch (e) {
        console.warn('Web Audio API not supported or blocked:', e);
    }
};

const PublicEmployeeDashboardPage = () => {
    const { slug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { appTheme, setAppTheme, toggleAppTheme } = useContext(ThemeContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthReference);
    const monthOptions = useMemo(getMonthReferenceOptions, []);
    const [monthPickerOpen, setMonthPickerOpen] = useState(false);
    const monthPickerRef = useRef(null);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [activeTab, setActiveTab] = useState('agenda');
    const [activeLedger, setActiveLedger] = useState('faturamento');
    const [showScoreHistory, setShowScoreHistory] = useState(false);
    const [scoreHistory, setScoreHistory] = useState(null);
    const [scoreHistoryKey, setScoreHistoryKey] = useState(null);
    const [scoreHistoryLoading, setScoreHistoryLoading] = useState(false);
    const [scoreHistoryError, setScoreHistoryError] = useState(null);

    const [showRevenueHistory, setShowRevenueHistory] = useState(false);
    const [revenueHistory, setRevenueHistory] = useState(null);
    const [revenueHistoryKey, setRevenueHistoryKey] = useState(null);
    const [revenueHistoryLoading, setRevenueHistoryLoading] = useState(false);
    const [revenueHistoryError, setRevenueHistoryError] = useState(null);
    // Cada aba descarta respostas antigas quando o colaborador muda o período rapidamente.
    const scoreHistoryRequestRef = useRef(0);
    const revenueHistoryRequestRef = useRef(0);

    // O extrato é sempre por período, independente do mês do resumo. Abre em 1º do mês até hoje.
    const [extractRange, setExtractRange] = useState(() => getMonthDefaultRange(getCurrentMonthReference()));
    const [rangeDraft, setRangeDraft] = useState(() => getMonthDefaultRange(getCurrentMonthReference()));
    const [rangeError, setRangeError] = useState(null);
    const [rangePickerOpen, setRangePickerOpen] = useState(false);
    const rangePickerRef = useRef(null);
    const [selectedServiceDetails, setSelectedServiceDetails] = useState(null);
    const [serviceDetails, setServiceDetails] = useState([]);
    const [serviceDetailsLoading, setServiceDetailsLoading] = useState(false);
    // Recorte que vira PDF: só o conteúdo do detalhamento, sem o cabeçalho do modal nem os
    // controles, que não fazem sentido no papel.
    const serviceDetailsPrintRef = useRef(null);
    const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
    const [calendarDate, setCalendarDate] = useState(() => new Date());
    const [isMobileViewport, setIsMobileViewport] = useState(() => (
        window.matchMedia?.('(max-width: 575px)').matches || false
    ));
    const [weeklyAgenda, setWeeklyAgenda] = useState([]);
    const [weeklyAgendaLoading, setWeeklyAgendaLoading] = useState(false);
    const [weeklyAgendaError, setWeeklyAgendaError] = useState(null);
    // Bloqueios da própria agenda: carregados junto da semana e desenhados no calendário,
    // porque o que ele precisa ver é onde a agenda está fechada, não uma lista à parte.
    const [agendaBlocks, setAgendaBlocks] = useState([]);
    const [blockFormOpen, setBlockFormOpen] = useState(false);
    const [blockForm, setBlockForm] = useState(null);
    const [blockSaving, setBlockSaving] = useState(false);
    const [blockError, setBlockError] = useState(null);
    const [selectedBlock, setSelectedBlock] = useState(null);
    // Agendamento rápido do próprio colaborador: abre pelo clique na grade ou pelo botão "+".
    const [quickBookingOpen, setQuickBookingOpen] = useState(false);
    const [quickBookingStart, setQuickBookingStart] = useState(null);
    // Muda a cada agendamento criado no portal, para a semana visível recarregar.
    const [agendaRefreshKey, setAgendaRefreshKey] = useState(0);
    // Agendamentos que o próprio colaborador acabou de criar. A atualização automática do
    // painel avisa "Novo agendamento" para o que aparece de outro canal; para estes ele já
    // recebeu a confirmação na hora, então o aviso seria repetido.
    const selfCreatedAppointmentsRef = useRef(new Set());

    useEffect(() => {
        // O portal deve abrir escuro mesmo quando o painel administrativo foi usado no modo claro.
        setAppTheme('dark');
    }, [setAppTheme]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 575px)');
        const updateViewport = (event) => setIsMobileViewport(event.matches);
        setIsMobileViewport(mediaQuery.matches);
        mediaQuery.addEventListener?.('change', updateViewport);
        return () => mediaQuery.removeEventListener?.('change', updateViewport);
    }, []);

    useEffect(() => {
        const closeMonthPicker = (event) => {
            if (!monthPickerRef.current?.contains(event.target)) {
                setMonthPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', closeMonthPicker);
        return () => document.removeEventListener('mousedown', closeMonthPicker);
    }, []);

    useEffect(() => {
        const closeRangePicker = (event) => {
            if (!rangePickerRef.current?.contains(event.target)) {
                setRangePickerOpen(false);
            }
        };
        document.addEventListener('mousedown', closeRangePicker);
        return () => document.removeEventListener('mousedown', closeRangePicker);
    }, []);

    const storageKey = useMemo(
        () => `colmeia_portal_token_${slug}`,
        [slug]
    );

    // O companyUuid é necessário para a foto do colaborador e o link de agendamento.
    // Derivamos da empresa carregada (resolvida a partir do slug da URL).
    const companyUuid = companyInfo?.uuid;

    const moveCalendar = (days) => {
        const nextDate = addDays(calendarDate, days);
        setCalendarDate(nextDate);
        setWeekStart(getWeekStart(nextDate));
    };

    const goToToday = () => {
        const currentDate = new Date();
        setWeekStart(getWeekStart(currentDate));
        setCalendarDate(currentDate);
    };

    // Habilita "adicionar à tela inicial" como app (ícone da empresa, abre no portal).
    usePortalInstall(companyInfo);

    const portalToken = useMemo(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (!stored) return null;
            const { token, expiresAt } = JSON.parse(stored);
            if (expiresAt && new Date(expiresAt) < new Date()) {
                localStorage.removeItem(storageKey);
                return null;
            }
            return token;
        } catch {
            return null;
        }
    }, [storageKey]);

    useEffect(() => {
        let active = true;
        if (!portalToken || !('serviceWorker' in navigator) || !('PushManager' in window)) return undefined;
        navigator.serviceWorker.ready
            .then((registration) => registration.pushManager.getSubscription())
            .then((subscription) => { if (active) setPushEnabled(Boolean(subscription)); })
            .catch(() => {});
        return () => { active = false; };
    }, [portalToken]);

    const [soundEnabled, setSoundEnabled] = useState(() => {
        return localStorage.getItem('colmeia_portal_sound_enabled') === 'true';
    });
    const [notifications, setNotifications] = useState([]);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [browserPermission, setBrowserPermission] = useState(
        () => ('Notification' in window ? Notification.permission : 'unsupported')
    );
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushBusy, setPushBusy] = useState(false);
    const [pushMessage, setPushMessage] = useState('');
    // O addNotification é memorizado uma vez só e roda dentro da consulta periódica; a ref
    // entrega a ele o estado atual do push sem recriar o callback nem reiniciar a consulta.
    const pushEnabledRef = useRef(pushEnabled);
    useEffect(() => {
        pushEnabledRef.current = pushEnabled;
    }, [pushEnabled]);
    const notificationsRef = useRef(null);
    const notifiedEventIdsRef = useRef(new Set());
    const audioContextRef = useRef(null);

    useEffect(() => () => audioContextRef.current?.close(), []);

    useEffect(() => {
        if (!soundEnabled) return undefined;
        // A preferência pode vir salva de outra visita; o primeiro toque desta sessão
        // precisa liberar o áudio novamente para o navegador permitir o próximo aviso.
        const unlockSound = () => {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;
            try {
                const ctx = audioContextRef.current || new AudioCtx();
                audioContextRef.current = ctx;
                if (ctx.state === 'suspended') ctx.resume();
            } catch {
                // O sininho continua funcional mesmo sem Web Audio.
            }
        };
        document.addEventListener('pointerdown', unlockSound, { once: true });
        return () => document.removeEventListener('pointerdown', unlockSound);
    }, [soundEnabled]);

    useEffect(() => {
        if (!notificationsOpen) return undefined;
        const closeOutside = (event) => {
            if (!notificationsRef.current?.contains(event.target)) setNotificationsOpen(false);
        };
        document.addEventListener('mousedown', closeOutside);
        return () => document.removeEventListener('mousedown', closeOutside);
    }, [notificationsOpen]);

    const handleLogout = () => {
        localStorage.removeItem(storageKey);
        navigate(`/p/${slug}/portal/login`, { replace: true });
    };

    // rangeOrEvent aceita o evento de clique das abas do extrato, que chamam sem argumento útil.
    const openScoreHistory = async (rangeOrEvent = extractRange, forceReload = false) => {
        const range = rangeOrEvent?.start ? rangeOrEvent : extractRange;
        const historyKey = buildHistoryKey(range);
        setActiveTab('extrato');
        setActiveLedger('pontos');
        if (!portalToken) {
            navigate(`/p/${slug}/portal/login`, { replace: true });
            return;
        }
        const requestId = ++scoreHistoryRequestRef.current;
        // Mesmo voltando ao cache, invalida a consulta de outro período que ainda esteja em voo.
        if (!forceReload && scoreHistoryKey === historyKey && scoreHistory !== null && !scoreHistoryError) {
            setScoreHistoryLoading(false);
            return;
        }
        setScoreHistoryLoading(true);
        setScoreHistoryError(null);
        try {
            const res = await getCollaboratorPortalScoreHistory(portalToken, range);
            if (requestId !== scoreHistoryRequestRef.current) return;
            setScoreHistory(res.data);
            setScoreHistoryKey(historyKey);
        } catch (err) {
            if (requestId !== scoreHistoryRequestRef.current) return;
            if (err?.response?.status === 401) {
                localStorage.removeItem(storageKey);
                navigate(`/p/${slug}/portal/login`, { replace: true });
                return;
            }
            const msg = err?.response?.data?.message || err?.response?.data || err?.message || 'Erro ao carregar extrato.';
            setScoreHistoryError(typeof msg === 'string' ? msg : 'Erro ao carregar extrato.');
            setScoreHistory([]);
        } finally {
            if (requestId === scoreHistoryRequestRef.current) setScoreHistoryLoading(false);
        }
    };

    const openRevenueHistory = async (rangeOrEvent = extractRange, forceReload = false) => {
        const range = rangeOrEvent?.start ? rangeOrEvent : extractRange;
        const historyKey = buildHistoryKey(range);
        setActiveTab('extrato');
        setActiveLedger('faturamento');
        if (!portalToken) {
            navigate(`/p/${slug}/portal/login`, { replace: true });
            return;
        }
        const requestId = ++revenueHistoryRequestRef.current;
        // Mesmo voltando ao cache, invalida a consulta de outro período que ainda esteja em voo.
        if (!forceReload && revenueHistoryKey === historyKey && revenueHistory !== null && !revenueHistoryError) {
            setRevenueHistoryLoading(false);
            return;
        }
        setRevenueHistoryLoading(true);
        setRevenueHistoryError(null);
        try {
            const res = await getCollaboratorPortalRevenueHistory(portalToken, range);
            if (requestId !== revenueHistoryRequestRef.current) return;
            setRevenueHistory(res.data);
            setRevenueHistoryKey(historyKey);
        } catch (err) {
            if (requestId !== revenueHistoryRequestRef.current) return;
            if (err?.response?.status === 401) {
                localStorage.removeItem(storageKey);
                navigate(`/p/${slug}/portal/login`, { replace: true });
                return;
            }
            const msg = err?.response?.data?.message || err?.response?.data || err?.message || 'Erro ao carregar extrato.';
            setRevenueHistoryError(typeof msg === 'string' ? msg : 'Erro ao carregar extrato.');
            setRevenueHistory([]);
        } finally {
            if (requestId === revenueHistoryRequestRef.current) setRevenueHistoryLoading(false);
        }
    };

    const handleMonthChange = (eventOrMonth) => {
        const nextMonth = typeof eventOrMonth === 'string' ? eventOrMonth : eventOrMonth.target.value;
        // O mês comanda só o resumo (comissão, metas, serviços). O extrato tem período próprio.
        setSelectedMonth(nextMonth);
    };

    const reloadActiveLedger = (range) => {
        if (activeLedger === 'pontos') {
            openScoreHistory(range, true);
        } else {
            openRevenueHistory(range, true);
        }
    };

    const applyExtractRange = () => {
        const message = validateExtractRange(rangeDraft);
        setRangeError(message);
        if (message) return;

        setExtractRange(rangeDraft);
        setRangePickerOpen(false);
        reloadActiveLedger(rangeDraft);
    };

    /** Volta para o recorte padrão: do dia 1º do mês corrente até hoje. */
    const resetExtractRange = () => {
        const defaultRange = getMonthDefaultRange(getCurrentMonthReference());
        setExtractRange(defaultRange);
        setRangeDraft(defaultRange);
        setRangeError(null);
        setRangePickerOpen(false);
        reloadActiveLedger(defaultRange);
    };

    const openServiceDetails = async (service) => {
        setSelectedServiceDetails(service);
        setServiceDetailsLoading(true);
        try {
            // O detalhamento sai do card de serviços do resumo, que é mensal — aqui o recorte
            // continua sendo o mês, não o período do extrato.
            const response = await getCollaboratorPortalRevenueHistory(portalToken, { monthReference: selectedMonth });
            // Além do nome, o canal do card, pela mesma régua que monta os grupos: cobrado é
            // avulso, de graça é clube. Filtrando só pelo nome, clicar no card do clube com 84
            // atendimentos abria uma lista de 98, somando os avulsos, e a comissão exibida no
            // rodapé era a dos dois juntos.
            const matchesChannel = (entry) => {
                const paid = !entry.clubMember || Number(entry.total || 0) > 0;
                if (service.channel === 'club') {
                    return !paid;
                }
                if (service.channel === 'paid') {
                    return paid;
                }
                return true;
            };
            // Casa pelo id do produto quando ele vem na linha: nome se repete entre produtos
            // diferentes, e aí duas listas distintas caíam na mesma janela.
            const details = (response.data || []).filter((entry) => (
                (entry.productId != null && service.productId != null
                    ? entry.productId === service.productId
                    : entry.serviceName === service.productName)
                && matchesChannel(entry)
            ));
            setServiceDetails(details);
        } catch (requestError) {
            setServiceDetails([]);
        } finally {
            setServiceDetailsLoading(false);
        }
    };

    // Resumo do avulso por categoria, o mesmo que o gestor lê no relatório de comissão. É a
    // categoria que define o percentual da regra, então é por ela que o barbeiro confere se a
    // comissão saiu do jeito combinado: R$ 1.315,00 de SERVIÇO a 38% e R$ 1.057,50 de SERVIÇOS
    // EXTRAS a 38%, e não um único número de onde não dá para tirar conclusão nenhuma.
    const revenueLedgerByCategory = Object.entries(
        (revenueHistory || [])
            .filter((entry) => entry.productType !== 'PRODUCT'
                && (!entry.clubMember || Number(entry.total || 0) > 0))
            .reduce((groups, entry) => {
                const categoryName = entry.categoryName || 'Sem categoria';
                const current = groups[categoryName] || { revenue: 0, commission: 0 };
                return {
                    ...groups,
                    [categoryName]: {
                        revenue: current.revenue + Number(entry.total || 0),
                        commission: current.commission + Number(entry.commission || 0)
                    }
                };
            }, {})
    ).sort(([first], [second]) => first.localeCompare(second, 'pt-BR'));

    // Totais do extrato nas mesmas parcelas dos cards do resumo, pela mesma régua: cobrado é
    // avulso, prateleira é produto. Enquanto o extrato mostrava uma "receita do período"
    // única, o card de Avulsos (R$ 2.372,50) levava a uma lista que somava R$ 3.463,99,
    // porque ali entram também os produtos.
    const revenueLedgerTotals = (revenueHistory || []).reduce((totals, entry) => {
        const value = Number(entry.total || 0);
        if (entry.productType === 'PRODUCT') {
            return { ...totals, produtos: totals.produtos + value };
        }
        // O atendimento gratuito do plano soma zero e não entra em nenhuma das duas caixas:
        // o que ele rende aparece na do pote, que vem pronta do backend.
        const paid = !entry.clubMember || value > 0;
        return paid ? { ...totals, avulsos: totals.avulsos + value } : totals;
    }, { avulsos: 0, produtos: 0 });

    // O que separa avulso de clube é o atendimento ter sido cobrado, e não a marca de
    // assinatura do item: essa marca é ligada para qualquer desconto de plano, inclusive
    // parcial. Uma sobrancelha de R$ 25,00 que o assinante pagou R$ 22,50 é venda, comissiona
    // e entra no card de Avulsos; pela marca, ela caía no grupo Clube e a soma dos cards do
    // grupo ficava R$ 967,50 abaixo do card de Avulsos lá em cima. Clube, aqui, é o
    // atendimento que saiu de graça pelo plano - o que o pote remunera.
    const isPaidServiceCount = (item) => !item.subscription || Number(item.total || 0) > 0;

    // Um card por produto dentro do grupo. Sem consolidar, o mesmo serviço vinha em duas
    // linhas (a cobrada e a com desconto de plano) e reaparecia duas vezes lado a lado dentro
    // do mesmo grupo, que é exatamente a duplicidade que os grupos vieram resolver.
    const consolidateServiceCounts = (items, channel) => {
        const byProduct = new Map();
        items.forEach((item) => {
            const current = byProduct.get(item.productId);
            byProduct.set(item.productId, {
                ...item,
                channel,
                quantity: Number(current?.quantity || 0) + Number(item.quantity || 0),
                total: Number(current?.total || 0) + Number(item.total || 0)
            });
        });
        return [...byProduct.values()].sort((left, right) => right.quantity - left.quantity);
    };

    // Os mesmos três grupos do Extrato do colaborador (Relatórios > Extrato do colaborador),
    // com os rótulos que o barbeiro já lê no resto do portal: lá são "Serviços avulsos",
    // "Serviços de assinatura" e "Produtos". Grupo vazio não vira cabeçalho solto.
    const serviceCounts = dashboard?.monthServiceCounts || [];
    const serviceCountGroups = [
        ['Avulsos', consolidateServiceCounts(
            serviceCounts.filter((item) => item.productType === 'SERVICE' && isPaidServiceCount(item)), 'paid')],
        ['Clube', consolidateServiceCounts(
            serviceCounts.filter((item) => item.productType === 'SERVICE' && !isPaidServiceCount(item)), 'club')],
        // Produto do plano que saiu sem cobrança é raro e continua na prateleira, então o
        // grupo não se divide por canal: 'all' faz o detalhe listar as duas formas.
        ['Produtos', consolidateServiceCounts(
            serviceCounts.filter((item) => item.productType === 'PRODUCT'), 'all')]
    ].filter(([, groupItems]) => groupItems.length > 0);

    const moveSelectedMonth = (amount) => {
        const nextMonth = shiftMonthReference(selectedMonth, amount);
        if (monthOptions.includes(nextMonth)) handleMonthChange(nextMonth);
    };

    const toggleSound = () => {
        const newValue = !soundEnabled;
        setSoundEnabled(newValue);
        localStorage.setItem('colmeia_portal_sound_enabled', String(newValue));
        soundEnabledRef.current = newValue;
        if (!newValue) return;

        // O clique no ícone libera o áudio para os avisos posteriores nos navegadores que
        // bloqueiam reprodução automática antes da primeira interação.
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                const ctx = audioContextRef.current || new AudioCtx();
                audioContextRef.current = ctx;
                if (ctx.state === 'suspended') {
                    ctx.resume();
                }
            }
        } catch (e) {
            console.warn(e);
        }
    };

    const openNotifications = async () => {
        setNotificationsOpen((open) => !open);
        if (!('Notification' in window)) return;
        setBrowserPermission(Notification.permission);
        if (Notification.permission === 'default') {
            try {
                setBrowserPermission(await Notification.requestPermission());
            } catch {
                setBrowserPermission(Notification.permission);
            }
        }
    };

    const togglePushNotifications = async () => {
        if (!portalToken || !('serviceWorker' in navigator) || !('PushManager' in window)) {
            setPushMessage('Este navegador não oferece notificações push para o portal.');
            return;
        }
        setPushBusy(true);
        setPushMessage('');
        try {
            const registration = await navigator.serviceWorker.ready;
            const existing = await registration.pushManager.getSubscription();
            if (existing) {
                await unsubscribeCollaboratorPortalPush(portalToken, existing.endpoint);
                await existing.unsubscribe();
                setPushEnabled(false);
                setPushMessage('Notificações desativadas neste aparelho.');
                return;
            }
            const permission = await Notification.requestPermission();
            setBrowserPermission(permission);
            if (permission !== 'granted') {
                setPushMessage('Permita notificações nas configurações do navegador para ativar os avisos.');
                return;
            }
            const response = await getCollaboratorPortalPushPublicKey();
            const publicKey = String(response.data || '').replaceAll('"', '').trim();
            if (!publicKey) throw new Error('Notificações push ainda não estão configuradas no servidor.');
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: decodeVapidKey(publicKey)
            });
            await subscribeCollaboratorPortalPush(portalToken, subscription.toJSON());
            setPushEnabled(true);
            setPushMessage('Notificações ativadas neste aparelho.');
        } catch (error) {
            setPushMessage(error?.message || 'Não foi possível configurar as notificações neste aparelho.');
        } finally {
            setPushBusy(false);
        }
    };

    const addNotification = useCallback((type, appointment) => {
        if (!appointment?.appointmentUuid) return;
        const id = `${type}-${appointment.appointmentUuid}`;
        // Evita repetir o aviso nativo quando duas consultas terminam com o mesmo resultado.
        if (notifiedEventIdsRef.current.has(id)) return;
        notifiedEventIdsRef.current.add(id);
        const title = type === 'removed' ? 'Agendamento saiu da agenda'
            : type === 'created' ? 'Agendamento realizado' : 'Novo agendamento';
        const scheduled = appointment.scheduledDate
            ? new Date(appointment.scheduledDate).toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
            })
            : '';
        const service = type === 'created' ? '' : ` · ${formatServices(appointment)}`;
        const message = `${appointment.customerName || 'Cliente'}${service}${scheduled ? ` · ${scheduled}` : ''}`;
        const notice = { id, type, title, message, appointment, unread: true };

        setNotifications((current) => {
            return [notice, ...current].slice(0, 20);
        });

        if (type === 'created' || !('Notification' in window) || Notification.permission !== 'granted') return;
        // Com o Web Push ativo neste aparelho, o servidor já avisa a mesma entrada ou saída da
        // agenda pelo service worker. Criar também o aviso nativo aqui fazia o colaborador
        // receber dois avisos com o portal aberto. O sino e o som do portal continuam valendo.
        if (pushEnabledRef.current) return;
        try {
            const nativeNotice = new Notification(title, {
                body: message,
                icon: '/favicon.ico',
                tag: `collaborator-portal-${id}`
            });
            nativeNotice.onclick = () => {
                window.focus();
                setNotifications((current) => current.map((item) =>
                    item.id === id ? { ...item, unread: false } : item));
                setActiveTab('agenda');
                if (type !== 'removed') setSelectedAppointment(appointment);
                nativeNotice.close();
            };
        } catch {
            // O sino continua disponível nos navegadores que recusam notificações nativas.
        }
    }, []);

    const dashboardRef = useRef(null);
    const soundEnabledRef = useRef(soundEnabled);
    
    useEffect(() => {
        dashboardRef.current = dashboard;
    }, [dashboard]);
    
    useEffect(() => {
        soundEnabledRef.current = soundEnabled;
    }, [soundEnabled]);

    const groupedAppointments = useMemo(() => {
        if (!dashboard?.upcomingAppointments) return [];
        
        const groups = {};
        dashboard.upcomingAppointments.forEach(appointment => {
            if (!appointment.scheduledDate) return;
            const date = new Date(appointment.scheduledDate);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateKey = `${year}-${month}-${day}`;
            
            if (!groups[dateKey]) {
                groups[dateKey] = {
                    dateKey,
                    date,
                    items: []
                };
            }
            groups[dateKey].items.push(appointment);
        });
        
        return Object.values(groups).sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    }, [dashboard?.upcomingAppointments]);

    const weeklyCalendarEvents = useMemo(() => ([
        ...weeklyAgenda
            .filter((appointment) => appointment.scheduledDate)
            .map((appointment) => ({
                id: appointment.appointmentUuid,
                title: appointment.customerName || 'Cliente não informado',
                start: new Date(appointment.scheduledDate),
                end: getAppointmentEnd(appointment),
                resource: appointment
            })),
        // O bloqueio entra como evento na mesma grade: numa lista separada, ele veria a
        // agenda com um buraco e teria de procurar em outro lugar por que aquele horário
        // está fechado.
        ...agendaBlocks.map((block) => ({
            id: `block-${block.id}`,
            title: block.reason || 'Bloqueado',
            start: new Date(block.startDate),
            end: new Date(block.endDate),
            resource: { agendaBlock: block }
        }))
    ]), [weeklyAgenda, agendaBlocks]);

    const weeklyCalendarBounds = useMemo(() => {
        const visibleDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
        const configuredHours = visibleDays.flatMap((day) => getWorkingHoursForDay(dashboard?.workingHours, day));
        const openMinutes = configuredHours
            .filter((item) => item.isOpen !== false)
            .map((item) => timeToMinutes(item.openTime))
            .filter((value) => value !== null);
        const closeMinutes = configuredHours
            .filter((item) => item.isOpen !== false)
            .map((item) => timeToMinutes(item.closeTime))
            .filter((value) => value !== null);
        const eventStarts = weeklyCalendarEvents.map((event) => event.start.getHours() * 60 + event.start.getMinutes());
        const eventEnds = weeklyCalendarEvents.map((event) => event.end.getHours() * 60 + event.end.getMinutes());
        const earliestMinutes = Math.min(...openMinutes, ...eventStarts);
        const latestMinutes = Math.max(...closeMinutes, ...eventEnds);
        const minMinutes = Number.isFinite(earliestMinutes) ? Math.floor(earliestMinutes / 60) * 60 : 8 * 60;
        const maxMinutes = Number.isFinite(latestMinutes) ? Math.ceil(latestMinutes / 60) * 60 : 18 * 60;
        const min = new Date(weekStart);
        const max = new Date(weekStart);
        min.setHours(Math.floor(minMinutes / 60), minMinutes % 60, 0, 0);
        max.setHours(Math.floor(maxMinutes / 60), maxMinutes % 60, 0, 0);
        if (max <= min) max.setHours(min.getHours() + 1, min.getMinutes(), 0, 0);
        return { min, max };
    }, [dashboard?.workingHours, weekStart, weeklyCalendarEvents]);

    const getCalendarDayStyle = (date) => {
        const dayHours = getWorkingHoursForDay(dashboard?.workingHours, date);
        const classNames = [];
        if (dayHours.length > 0 && !dayHours.some((item) => item.isOpen !== false)) {
            classNames.push('collab-calendar-day--closed');
        }
        return classNames.length > 0 ? { className: classNames.join(' ') } : {};
    };

    useEffect(() => {
        if (!portalToken) return;
        const startDate = formatDateKey(weekStart);
        const endDate = formatDateKey(addDays(weekStart, 6));
        let active = true;
        setWeeklyAgendaLoading(true);
        setWeeklyAgendaError(null);
        getCollaboratorPortalAgenda(portalToken, startDate, endDate)
            .then((response) => {
                if (active) setWeeklyAgenda(response.data || []);
            })
            .catch((requestError) => {
                if (!active) return;
                if (requestError?.response?.status === 401) {
                    localStorage.removeItem(storageKey);
                    navigate(`/p/${slug}/portal/login`, { replace: true });
                    return;
                }
                setWeeklyAgendaError('Não foi possível carregar a agenda da semana.');
            })
            .finally(() => {
                if (active) setWeeklyAgendaLoading(false);
            });
        return () => { active = false; };
    }, [navigate, portalToken, slug, storageKey, weekStart, agendaRefreshKey]);

    // Recarrega os bloqueios da semana visível. Fica separado do carregamento da agenda para
    // poder ser chamado de novo depois de criar ou reabrir um bloqueio, sem repuxar a agenda.
    const reloadAgendaBlocks = useCallback(() => {
        if (!portalToken || dashboard?.employeeCanBlockAgenda !== true) {
            setAgendaBlocks([]);
            return Promise.resolve();
        }
        return getCollaboratorPortalAgendaBlocks(portalToken, {
            start: formatDateKey(weekStart),
            end: formatDateKey(addDays(weekStart, 6))
        })
            .then((response) => setAgendaBlocks(response.data || []))
            // Falha aqui não pode derrubar a agenda: sem os bloqueios a semana ainda é útil.
            .catch(() => setAgendaBlocks([]));
    }, [portalToken, weekStart, dashboard?.employeeCanBlockAgenda]);

    useEffect(() => {
        reloadAgendaBlocks();
    }, [reloadAgendaBlocks]);

    /**
     * Abre o formulário no dia de hoje sempre que hoje estiver na semana visível, que é o
     * caso comum: o barbeiro bloqueia o dia em que está. Só quando ele navegou para outra
     * semana o padrão passa a ser o dia que está olhando - no desktop o início daquela
     * semana, que antes era usado sempre e trazia domingo passado, uma data que o servidor
     * recusa por ser no passado.
     */
    // Liberado por empresa em Configurações > Agendamento. Ausente conta como liberado, o
    // mesmo default do servidor.
    const canSchedule = dashboard?.employeeCanSchedule !== false;

    /** Abre o agendamento rápido no horário informado (ou no próximo horário redondo). */
    const openQuickBooking = (start = null) => {
        setQuickBookingStart(start);
        setQuickBookingOpen(true);
    };

    /**
     * Clique num horário vazio da grade. Horário que já passou é ignorado: o servidor
     * recusaria, e abrir o formulário só para mostrar o erro no fim é pior.
     */
    const handleCalendarSlotSelect = (slotInfo) => {
        if (!canSchedule || !slotInfo?.start || slotInfo.start < new Date()) return;
        openQuickBooking(slotInfo.start);
    };

    const handleQuickBookingCreated = (created) => {
        if (created?.appointmentUuid) {
            selfCreatedAppointmentsRef.current.add(created.appointmentUuid);
        }
        setQuickBookingOpen(false);
        addNotification('created', created);
        setAgendaRefreshKey((key) => key + 1);
        loadData(true);
    };

    const openBlockForm = () => {
        const today = new Date();
        const visibleStart = weekStart;
        const visibleEnd = addDays(weekStart, 6);
        const todayIsVisible = formatDateKey(today) >= formatDateKey(visibleStart)
            && formatDateKey(today) <= formatDateKey(visibleEnd);
        const day = formatDateKey(todayIsVisible ? today : (isMobileViewport ? calendarDate : visibleStart));

        setBlockError(null);
        setBlockForm({ date: day, startTime: '09:00', endTime: '18:00', reason: '', branchId: '' });
        setBlockFormOpen(true);
    };

    const submitBlockForm = () => {
        if (!blockForm?.date || !blockForm?.startTime || !blockForm?.endTime) {
            setBlockError('Informe o dia e o horário que deseja bloquear.');
            return;
        }
        if (blockForm.endTime <= blockForm.startTime) {
            setBlockError('O fim do bloqueio deve ser depois do início.');
            return;
        }

        setBlockSaving(true);
        setBlockError(null);
        createCollaboratorPortalAgendaBlock(portalToken, {
            startDate: `${blockForm.date}T${blockForm.startTime}:00`,
            endDate: `${blockForm.date}T${blockForm.endTime}:00`,
            reason: blockForm.reason?.trim() || null,
            branchId: blockForm.branchId ? Number(blockForm.branchId) : null
        })
            .then(() => {
                setBlockFormOpen(false);
                setBlockForm(null);
                return reloadAgendaBlocks();
            })
            // A mensagem do servidor é a que explica o motivo real (horário com atendimento
            // marcado, período já bloqueado, recurso desativado), então ela vai para a tela.
            .catch((requestError) => setBlockError(
                requestError?.response?.data?.message || 'Não foi possível bloquear este horário.'))
            .finally(() => setBlockSaving(false));
    };

    const removeAgendaBlock = (block) => {
        setBlockSaving(true);
        cancelCollaboratorPortalAgendaBlock(portalToken, block.id)
            .then(() => {
                setSelectedBlock(null);
                return reloadAgendaBlocks();
            })
            .catch(() => setBlockError('Não foi possível reabrir este horário.'))
            .finally(() => setBlockSaving(false));
    };

    const loadData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [companyResponse, dashboardResponse] = await Promise.all([
                getCompanyInfo(slug),
                getCollaboratorPortalDashboard(portalToken, selectedMonth)
            ]);
            setCompanyInfo(companyResponse.data);

            const newDashboard = dashboardResponse.data;

            // Renovação silenciosa: o backend devolve um token novo quando o atual passa da metade
            // da validade. Atualizamos o localStorage para que a próxima abertura use o token renovado
            // — quem usa o portal com frequência nunca precisa relogar.
            if (newDashboard?.renewedToken) {
                localStorage.setItem(storageKey, JSON.stringify({
                    token: newDashboard.renewedToken,
                    expiresAt: newDashboard.renewedTokenExpiresAt
                }));
            }

            const oldDashboard = dashboardRef.current;
            
            // Trocar o mês substitui a fotografia inteira da agenda; diferenças entre meses
            // não são novos agendamentos nem saídas da agenda. Os avisos só valem para a
            // atualização automática do mesmo mês que o colaborador está visualizando.
            if (silent && oldDashboard && oldDashboard.monthReference === newDashboard.monthReference) {
                const oldUpcoming = oldDashboard.upcomingAppointments || [];
                const newUpcoming = newDashboard.upcomingAppointments || [];
                
                const oldIds = new Set(oldUpcoming.map(a => a.appointmentUuid));
                const newIds = new Set(newUpcoming.map(a => a.appointmentUuid));
                
                // 1. Novo agendamento
                const addedAppointments = newUpcoming.filter(a => !oldIds.has(a.appointmentUuid)
                    && !selfCreatedAppointmentsRef.current.has(a.appointmentUuid));
                
                // 2. Saídas da agenda. A lista tem teto de 20, então, quando está cheia,
                // uma ausência pode ser só deslocamento pelo limite e não gera aviso.
                const nowLimit = new Date();
                const removedAppointments = newUpcoming.length >= 20 ? [] : oldUpcoming.filter(a => {
                    const isFuture = new Date(a.scheduledDate) > nowLimit;
                    return isFuture && !newIds.has(a.appointmentUuid);
                });
                
                if (addedAppointments.length > 0) {
                    addedAppointments.forEach((appt) => addNotification('new', appt));
                    if (soundEnabledRef.current) {
                        playNotificationSound('new', audioContextRef.current);
                    }
                }
                
                if (removedAppointments.length > 0) {
                    removedAppointments.forEach((appt) => addNotification('removed', appt));
                    if (soundEnabledRef.current) {
                        playNotificationSound('cancel', audioContextRef.current);
                    }
                }
            }
            
            setDashboard(newDashboard);
        } catch (requestError) {
            if (!silent) {
                if (requestError?.response?.status === 401) {
                    // Token expirado ou inválido — redireciona para login
                    localStorage.removeItem(storageKey);
                    navigate(`/p/${slug}/portal/login`, { replace: true });
                    return;
                } else if (requestError?.response?.status === 404) {
                    setError('Colaborador ou empresa não encontrados.');
                } else {
                    setError('Não foi possível carregar a agenda pública agora.');
                }
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [slug, portalToken, storageKey, navigate, selectedMonth, addNotification]);

    useEffect(() => {
        if (!slug) {
            setError('Link inválido para o portal do colaborador.');
            setLoading(false);
            return;
        }

        if (!portalToken) {
            navigate(`/p/${slug}/portal/login`, { replace: true });
            return;
        }

        loadData(false);

        const intervalId = setInterval(() => {
            loadData(true);
        }, 30000);

        return () => {
            clearInterval(intervalId);
        };
    }, [slug, portalToken, loadData, navigate]);

    const employeeImageUrl = useMemo(() => {
        if (!dashboard?.employee?.id || !dashboard?.employee?.hasImage) {
            return null;
        }
        return getEmployeePhotoUrl(companyUuid, dashboard.employee.id);
    }, [companyUuid, dashboard]);

    // As mesmas parcelas do "Faturamento gerado" do relatório do gestor, para os dois lados
    // conversarem sobre o mesmo número. Serviço e produto vêm separados porque quem vende na
    // prateleira precisa enxergar a própria venda, e não diluída no total do mês.
    const revenueSummaryItems = useMemo(() => ([
        // O total do faturamento ("Total da cadeira") não entra nesta lista: ele fica num card
        // próprio acima da grade, sem linha de ganho, porque não é mais uma parcela. Aqui
        // ficam só os cards cujas linhas de ganho somam o "Seus ganhos" do topo.
        {
            // "Avulsos" é o nome que essa parcela tem no relatório de comissão. Vale mais a
            // pessoa e o gestor usarem a mesma palavra ao conferir o mês do que o portal ter
            // um rótulo próprio; o hint abaixo é quem explica o que entra ali.
            //
            // O valor grande é o que ele VENDEU, e o `earning` é o que aquilo virou no bolso
            // dele. Sem essa segunda linha, os cards não somavam o total em destaque no topo
            // (R$ 2.372,50 + R$ 1.091,49 + R$ 4.637,21 dá R$ 8.101,20, e o topo dizia
            // R$ 5.538,76), e barbeiro confere essa conta na mão.
            label: 'Avulsos',
            value: formatCurrency(dashboard?.monthRevenueServices ?? 0),
            hint: 'Serviços cobrados do cliente, fora do clube',
            // Neutro: é venda, a base sobre a qual a comissão incide.
            tone: 'sale',
            earningLabel: 'Sua comissão',
            earning: formatCurrency(dashboard?.normalCommission ?? 0)
        },
        {
            label: 'Produtos',
            value: formatCurrency(dashboard?.monthRevenueProducts ?? 0),
            hint: 'Produtos que você vendeu',
            tone: 'sale',
            earningLabel: 'Sua comissão',
            earning: formatCurrency(dashboard?.commissionProducts ?? 0)
        },
        {
            // Aqui vai o que ele recebe do clube, e não a fatia da mensalidade que ele
            // produziu. O valor produzido é leitura de dono: o barbeiro não tem o que fazer
            // com "a casa recebeu R$ 11.593,03 por sua causa", e o número grande ao lado dos
            // outros dava a impressão de um dinheiro que seria dele.
            //
            // Diferente dos dois acima, aqui o valor grande já é ganho, e a linha de baixo
            // diz isso: senão ele lê os três cards como se fossem todos venda.
            label: 'Pote',
            value: formatCurrency(dashboard?.subscriptionEarnings ?? 0),
            hint: 'Sua parte nas mensalidades, dividida pelos pontos dos atendimentos do clube',
            // Destaque: aqui o valor grande já é dinheiro dele, ao contrário dos dois ao lado.
            tone: 'earning',
            earningLabel: 'Já é seu',
            earning: formatCurrency(dashboard?.subscriptionEarnings ?? 0)
        },
        // O adicional combinado só aparece para quem tem acordo. Para os demais seria um card
        // zerado que levanta a pergunta errada ("por que eu não tenho isso?") num lugar onde
        // ninguém está para responder.
        ...(Number(dashboard?.potBonus || 0) > 0 ? [{
            label: 'Adicional combinado',
            value: formatCurrency(dashboard?.potBonus ?? 0),
            hint: 'O que você combinou com a barbearia sobre o que você faz de clube. Quem paga é ela, e não sai do pote dividido entre a equipe',
            tone: 'earning',
            earningLabel: 'Já é seu',
            earning: formatCurrency(dashboard?.potBonus ?? 0)
        }] : []),
        // Bonificação também só aparece para quem recebeu no mês, pelo mesmo motivo do
        // adicional. Já vem somada em "Seus ganhos no mês" pelo backend, e a linha "Já é seu"
        // mantém a conta dos cards fechando com o topo.
        ...(Number(dashboard?.monthBonuses || 0) > 0 ? [{
            label: 'Bonificação',
            value: formatCurrency(dashboard?.monthBonuses ?? 0),
            hint: 'Valor combinado com a casa para este mês',
            tone: 'earning',
            earningLabel: 'Já é seu',
            earning: formatCurrency(dashboard?.monthBonuses ?? 0)
        }] : []),
        // Não há card de agendado nem de receita prevista. Os dois eram faturamento futuro, e
        // o bloco financeiro fala de dinheiro que já é dele: o que interessa do que está
        // marcado é quanto vai render, não quanto a casa vai receber. A agenda em si continua
        // logo abaixo, em quantidade de atendimentos, que é como ele a lê.
    ]), [dashboard]);

    const activitySummaryItems = useMemo(() => ([
        { label: 'Hoje', value: dashboard?.todayAppointments || 0 },
        { label: 'Próximos', value: dashboard?.monthUpcomingAppointments || 0 },
        { label: 'Concluídos', value: dashboard?.monthCompletedAppointments || 0 },
    ]), [dashboard]);

    // Cada card acima já mostra o que ele vendeu e o que aquilo virou de ganho, então repetir
    // as três parcelas aqui embaixo era a mesma conta duas vezes na mesma tela. Sobra a
    // pontuação, que não aparece em card nenhum e é a régua do rateio do pote: sem ela, o
    // valor do pote parece ter saído do nada.
    const earningsSummaryItems = useMemo(() => ([
        {
            label: 'Pontuação',
            value: `${dashboard?.subscriptionScore ?? 0} pts`,
            hint: 'Atendimentos do clube que você fez. É por eles que o pote é dividido'
        }
    ]), [dashboard]);

    const welcomeMessage = useMemo(() => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Bom dia';
        if (hour < 18) return 'Boa tarde';
        return 'Boa noite';
    }, []);

    const todayLabel = useMemo(() => new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long', day: 'numeric', month: 'long'
    }).format(new Date()), []);

    if (loading) {
        return (
            <div className="public-booking-page employee-dashboard-page employee-dashboard-loading-screen">
                <Container className="employee-dashboard-container text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 employee-dashboard-loading">Carregando agenda do colaborador...</p>
                </Container>
            </div>
        );
    }

    if (error) {
        return (
            <div className="public-booking-page employee-dashboard-page">
                <Container className="py-5">
                    <Alert variant="danger" className="text-center">
                        {error}
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="public-booking-page employee-dashboard-page">
            <Container className="booking-container employee-dashboard-container">
                <div className="employee-dashboard-page-title">
                    <div className="employee-dashboard-brand">
                        <img src={colmeiaLogo} alt="Colmeia" className="employee-dashboard-brand__logo" />
                        <h1 className="employee-dashboard-page-title__heading">Portal do Colaborador</h1>
                    </div>
                    <div className="employee-dashboard-header-actions">
                        <button
                            type="button"
                            className={`employee-dashboard-header-icon${soundEnabled ? ' is-active' : ''}`}
                            onClick={toggleSound}
                            aria-label={soundEnabled ? 'Desativar som das notificações' : 'Ativar som das notificações'}
                            aria-pressed={soundEnabled}
                            title={soundEnabled ? 'Desativar som' : 'Ativar som'}
                        >
                            <i className={`fa-solid ${soundEnabled ? 'fa-volume-high' : 'fa-volume-xmark'}`}></i>
                        </button>
                        <div className="employee-dashboard-notifications" ref={notificationsRef}>
                            <button
                                type="button"
                                className="employee-dashboard-header-icon"
                                onClick={openNotifications}
                                aria-label="Notificações"
                                aria-expanded={notificationsOpen}
                                title="Notificações"
                            >
                                <i className="fa-regular fa-bell"></i>
                                {notifications.some((notice) => notice.unread) && (
                                    <span className="employee-dashboard-notifications__badge">
                                        {notifications.filter((notice) => notice.unread).length}
                                    </span>
                                )}
                            </button>
                            {notificationsOpen && (
                                <div className="employee-dashboard-notifications__panel">
                                    <div className="employee-dashboard-notifications__heading">
                                        <strong>Notificações</strong>
                                        {notifications.some((notice) => notice.unread) && (
                                            <button type="button" onClick={() => setNotifications((current) =>
                                                current.map((notice) => ({ ...notice, unread: false })))}>
                                                Marcar lidas
                                            </button>
                                        )}
                                    </div>
                                    <small>{browserPermission === 'granted'
                                        ? 'Avisos do navegador ativados.'
                                        : browserPermission === 'denied'
                                            ? 'Libere notificações deste site no navegador para receber avisos fora da aba.'
                                            : browserPermission === 'unsupported'
                                                ? 'Este navegador mostra os avisos no sino.'
                                                : 'Permita notificações quando o navegador pedir.'}</small>
                                    <button type="button" onClick={togglePushNotifications} disabled={pushBusy}
                                        className="employee-dashboard-notifications__push-toggle">
                                        {pushBusy ? 'Aguarde…' : pushEnabled ? 'Desativar avisos neste aparelho' : 'Ativar avisos neste aparelho'}
                                    </button>
                                    {pushMessage && <small role="status">{pushMessage}</small>}
                                    {notifications.length === 0 ? (
                                        <p>Nenhuma novidade desde que você abriu o portal.</p>
                                    ) : notifications.map((notice) => (
                                        <button
                                            type="button"
                                            key={notice.id}
                                            className={`employee-dashboard-notifications__item${notice.unread ? ' is-unread' : ''}`}
                                            onClick={() => {
                                                setNotifications((current) => current.map((item) =>
                                                    item.id === notice.id ? { ...item, unread: false } : item));
                                                setActiveTab('agenda');
                                                if (notice.type === 'new') setSelectedAppointment(notice.appointment);
                                                setNotificationsOpen(false);
                                            }}
                                        >
                                            <span className="employee-dashboard-notifications__item-title">
                                                <i className={`fa-solid ${notice.type === 'removed' ? 'fa-calendar-xmark' : 'fa-calendar-check'}`}></i>
                                                {notice.title}
                                            </span>
                                            <small>{notice.message}</small>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="portal-theme-toggle employee-dashboard-theme-toggle"
                            onClick={toggleAppTheme}
                            aria-label={appTheme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
                            title={appTheme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
                        >
                            <i className={`fa-solid ${appTheme === 'dark' ? 'fa-sun' : 'fa-moon'}`}></i>
                        </button>
                        <button type="button" className="employee-dashboard-logout-header" onClick={handleLogout} aria-label="Sair do portal" title="Sair do portal">
                            <i className="fa-solid fa-right-from-bracket"></i>
                        </button>
                    </div>
                </div>

                <div className="employee-dashboard-profile-shell employee-dashboard-profile-tab-visible">
                    <div className="employee-dashboard-avatar-wrap">
                        <div className="employee-dashboard-avatar employee-dashboard-avatar--spotlight">
                            {employeeImageUrl ? (
                                <img src={employeeImageUrl} alt={dashboard?.employeeName} />
                            ) : (
                                <span>{dashboard?.employeeName?.charAt(0) || 'C'}</span>
                            )}
                        </div>
                    </div>

                    <div className="employee-dashboard-profile-card__identity">
                        <span className="employee-dashboard-welcome">{welcomeMessage},</span>
                        <h2>{dashboard?.employeeName}</h2>
                        <small>{companyInfo?.name}</small>
                        <div className="employee-dashboard-today">
                            <i className="fa-regular fa-calendar"></i>
                            <span>{todayLabel}</span>
                            <span className="employee-dashboard-status"><i className="fa-solid fa-circle"></i> Portal ativo</span>
                        </div>
                    </div>
                </div>

                <nav className="employee-dashboard-tabs" aria-label="Navegação do portal">
                    <button
                        type="button"
                        aria-current={activeTab === 'agenda' ? 'page' : undefined}
                        className={`employee-dashboard-tab ${activeTab === 'agenda' ? 'active' : ''}`}
                        onClick={() => setActiveTab('agenda')}
                    >
                        <i className="fa-regular fa-calendar-check"></i>
                        Agenda
                    </button>
                    <button
                        type="button"
                        aria-current={activeTab === 'resumo' ? 'page' : undefined}
                        className={`employee-dashboard-tab ${activeTab === 'resumo' ? 'active' : ''}`}
                        onClick={() => setActiveTab('resumo')}
                    >
                        <i className="fa-solid fa-chart-simple"></i>
                        Resumo
                    </button>
                    <button
                        type="button"
                        aria-current={activeTab === 'extrato' ? 'page' : undefined}
                        className={`employee-dashboard-tab ${activeTab === 'extrato' ? 'active' : ''}`}
                        onClick={openRevenueHistory}
                    >
                        <i className="fa-solid fa-list-check"></i>
                        Extrato
                    </button>
                </nav>

                <AddToHomeScreenHint />

                <section className={`employee-dashboard-overview shadow-sm${activeTab !== 'resumo' ? ' employee-dashboard-tab-hidden' : ''}`}>
                    <div className="employee-dashboard-month-selector">
                        <button
                            type="button"
                            className="employee-dashboard-month-selector__button"
                            onClick={() => moveSelectedMonth(-1)}
                            disabled={!monthOptions.includes(shiftMonthReference(selectedMonth, -1))}
                            aria-label="Mês anterior"
                            title="Mês anterior"
                        >
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>
                        <div className="employee-dashboard-month-picker" ref={monthPickerRef}>
                            <button
                                id="employee-dashboard-month"
                                type="button"
                                className="employee-dashboard-month-picker__trigger"
                                onClick={() => setMonthPickerOpen((open) => !open)}
                                aria-haspopup="listbox"
                                aria-expanded={monthPickerOpen}
                                aria-label="Selecionar mês de referência"
                            >
                                {formatMonthReference(selectedMonth)}
                                <i className={`fa-solid fa-chevron-${monthPickerOpen ? 'up' : 'down'}`}></i>
                            </button>
                            {monthPickerOpen && (
                                <div className="employee-dashboard-month-picker__menu" role="listbox" aria-label="Meses disponíveis">
                                    {monthOptions.map((month) => (
                                        <button
                                            key={month}
                                            type="button"
                                            role="option"
                                            aria-selected={selectedMonth === month}
                                            className={`employee-dashboard-month-picker__option${selectedMonth === month ? ' active' : ''}`}
                                            onClick={() => {
                                                handleMonthChange(month);
                                                setMonthPickerOpen(false);
                                            }}
                                        >
                                            {formatMonthReference(month)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="employee-dashboard-month-selector__button"
                            onClick={() => moveSelectedMonth(1)}
                            disabled={!monthOptions.includes(shiftMonthReference(selectedMonth, 1))}
                            aria-label="Próximo mês"
                            title="Próximo mês"
                        >
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    </div>
                    {/* O número grande é o que ele recebe, não o que a casa faturou. O portal é
                        a tela dele: abrir e ver o faturamento em destaque é leitura de dono, e
                        obrigava a procurar a comissão lá embaixo para responder à única pergunta
                        que ele veio fazer. O faturamento continua logo abaixo, porque é a base
                        da comissão e é o que ele consegue mexer no dia a dia. */}
                    <div className="employee-dashboard-overview__primary">
                        <span className="employee-dashboard-overview__eyebrow"><i className="fa-solid fa-wallet" aria-hidden="true"></i>Seus ganhos no mês</span>
                        <strong>{formatCurrency(dashboard?.monthEarningsTotal ?? 0)}</strong>
                        <small>Sua comissão dos avulsos e dos produtos, mais o pote. O valor cresce conforme você atende.</small>
                        {/* Vale e consumo só aparecem para quem tem. O número grande continua sendo
                            o ganho cheio e o desconto vem embaixo, com o valor a receber: os dois
                            juntos são o que ele confere na hora do acerto, e mostrar só o líquido
                            esconderia quanto ele de fato produziu no mês. */}
                        {(Number(dashboard?.monthAdvances ?? 0) > 0 || Number(dashboard?.monthConsumptions ?? 0) > 0) && (
                            <div className="employee-dashboard-overview__advances">
                                {Number(dashboard?.monthAdvances ?? 0) > 0 && (
                                    <div className="employee-dashboard-overview__advances-row">
                                        <span className="employee-dashboard-overview__advances-label">
                                            <i className="fa-solid fa-hand-holding-dollar"></i>
                                            Vales que você já pegou
                                        </span>
                                        <strong className="employee-dashboard-overview__advances-value">
                                            - {formatCurrency(dashboard.monthAdvances)}
                                        </strong>
                                    </div>
                                )}
                                {/* Consumo (refrigerante, água) que ficou para descontar.
                                    O que ele pagou na hora no caixa não aparece: já está acertado. */}
                                {Number(dashboard?.monthConsumptions ?? 0) > 0 && (
                                    <div className="employee-dashboard-overview__advances-row">
                                        <span className="employee-dashboard-overview__advances-label">
                                            <i className="fa-solid fa-mug-hot"></i>
                                            Consumos a descontar
                                        </span>
                                        <strong className="employee-dashboard-overview__advances-value">
                                            - {formatCurrency(dashboard.monthConsumptions)}
                                        </strong>
                                    </div>
                                )}
                                {/* O "a receber" é a linha do acerto: fica embaixo, separada
                                    por um filete, com o peso de resultado. */}
                                <div className="employee-dashboard-overview__advances-row employee-dashboard-overview__advances-row--net">
                                    <span className="employee-dashboard-overview__advances-label">A receber</span>
                                    <strong className="employee-dashboard-overview__advances-net">
                                        {formatCurrency(dashboard?.monthNetEarnings ?? 0)}
                                    </strong>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="employee-dashboard-overview__content">
                        {/* Total da cadeira: o mesmo Total do "Faturamento gerado" da tela
                            Comissões do gestor (avulsos, produtos e a parte das mensalidades
                            do clube que veio dos atendimentos dele). Fica fora da grade e sem
                            linha de ganho, porque não é mais uma parcela: as linhas de ganho
                            dos cards abaixo continuam somando o "Seus ganhos" do topo. */}
                        <div className="employee-dashboard-overview-item employee-dashboard-overview-item--sale employee-dashboard-overview-item--chair">
                            <span>Total da cadeira</span>
                            <strong>{formatCurrency(dashboard?.monthRevenueRealized ?? 0)}</strong>
                            <small>Tudo o que sua cadeira faturou no mês: avulsos, produtos e clube</small>
                        </div>
                        <div className="employee-dashboard-overview__grid">
                            {revenueSummaryItems.map((item) => {
                                // O extrato lista o que passou pela comanda, então o atalho vive no
                                // card de avulsos. Pote e previsão são contas derivadas e não têm
                                // uma lista de lançamentos para abrir.
                                const isFaturado = item.label === 'Avulsos';
                                return (
                                    <div
                                        key={item.label}
                                        className={`employee-dashboard-overview-item employee-dashboard-overview-item--${item.tone || 'sale'}${isFaturado ? ' employee-dashboard-overview-item--clickable' : ''}`}
                                        onClick={isFaturado ? openRevenueHistory : undefined}
                                        role={isFaturado ? 'button' : undefined}
                                        tabIndex={isFaturado ? 0 : undefined}
                                        onKeyDown={isFaturado ? (e) => e.key === 'Enter' && openRevenueHistory() : undefined}
                                    >
                                        <span>
                                            {item.label}
                                            {isFaturado && <i className="fa-solid fa-chevron-right ms-1" style={{ fontSize: '0.65rem', opacity: 0.5 }}></i>}
                                        </span>
                                        <strong>{item.value}</strong>
                                        <small>{item.hint}</small>
                                        {/* A conta que o barbeiro faz na mão: o que ele vendeu
                                            em cima, o que aquilo rendeu para ele embaixo. As
                                            três linhas de ganho somam o valor em destaque no
                                            topo da tela. */}
                                        {item.earning && (
                                            <span className="employee-dashboard-overview-item__earning">
                                                {item.earningLabel}
                                                <strong>{item.earning}</strong>
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="employee-dashboard-overview__split">
                            <div className="employee-dashboard-compact-block">
                                <div className="employee-dashboard-compact-block__header">
                                    <h3>Agenda</h3>
                                    <span>{formatMonthReference(dashboard?.monthReference)}</span>
                                </div>
                                <div className="employee-dashboard-stat-list">
                                    {activitySummaryItems.map((item) => (
                                        <div key={item.label} className="employee-dashboard-stat-list__row">
                                            <span>{item.label}</span>
                                            <strong>{item.value}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="employee-dashboard-compact-block">
                                <div className="employee-dashboard-compact-block__header">
                                    <h3>Pontos do clube</h3>
                                    <span>{formatMonthReference(dashboard?.monthReference)}</span>
                                </div>
                                <div className="employee-dashboard-stat-list">
                                    {earningsSummaryItems.map((item) => {
                                        const isScore = item.label === 'Pontuação';
                                        return (
                                            <div
                                                key={item.label}
                                                className={`employee-dashboard-stat-list__row${isScore ? ' employee-dashboard-stat-list__row--clickable' : ''}`}
                                                onClick={isScore ? openScoreHistory : undefined}
                                                role={isScore ? 'button' : undefined}
                                                tabIndex={isScore ? 0 : undefined}
                                                onKeyDown={isScore ? (e) => e.key === 'Enter' && openScoreHistory() : undefined}
                                            >
                                                <span>
                                                    {item.label}
                                                    <small>{item.hint}</small>
                                                </span>
                                                <strong>
                                                    {item.value}
                                                    {isScore && <i className="fa-solid fa-chevron-right ms-1" style={{ fontSize: '0.7rem', opacity: 0.5 }}></i>}
                                                </strong>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {dashboard?.monthServiceCounts?.length > 0 && (
                            <div className="employee-dashboard-compact-block">
                                <div className="employee-dashboard-compact-block__header">
                                        <h3>Serviços realizados</h3>
                                    <span>{formatMonthReference(dashboard?.monthReference)}</span>
                                </div>
                                {/* Um card por serviço/produto, do mais frequente para o menos.
                                    É a contagem que a casa mantinha à mão num quadro na parede, e o
                                    card deixa a quantidade legível de relance, que é como o quadro
                                    era lido. Reusa employee-dashboard-overview-item para herdar a
                                    tipografia dos demais indicadores do resumo.

                                    Os cards vêm sob os mesmos três grupos do Extrato do colaborador
                                    que o gestor abre (avulso, clube e produtos), na linguagem que o
                                    resto do portal já usa. O mesmo serviço aparece em dois grupos
                                    quando foi feito das duas formas: numa grade única eles caíam
                                    lado a lado, sem nada explicando por que "Corte" estava duas
                                    vezes com números diferentes. */}
                                {serviceCountGroups.map(([groupLabel, groupItems]) => (
                                    <Fragment key={groupLabel}>
                                        {/* Nome do grupo à esquerda e, à direita, quantos
                                            atendimentos e quanto somam. O total existe para
                                            conferência: Avulsos e Produtos batem com os cards
                                            do resumo financeiro lá em cima, e Clube sai zerado
                                            porque é o atendimento que o pote remunera. */}
                                        <div className="employee-dashboard-services__group">
                                            <span className="employee-dashboard-services__group-name">{groupLabel}</span>
                                            <span className="employee-dashboard-services__group-summary">
                                                {formatQuantity(groupItems.reduce((total, item) => total + Number(item.quantity || 0), 0))}
                                                {' · '}
                                                {formatCurrency(groupItems.reduce((total, item) => total + Number(item.total || 0), 0))}
                                            </span>
                                        </div>
                                        <div className="employee-dashboard-services__grid">
                                            {groupItems.map((item) => (
                                        <div
                                            key={`${groupLabel}-${item.productId}`}
                                            className="employee-dashboard-overview-item employee-dashboard-service-card employee-dashboard-service-card--clickable"
                                            onClick={() => openServiceDetails(item)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(event) => event.key === 'Enter' && openServiceDetails(item)}
                                            title={item.productName}
                                        >
                                            <span className="employee-dashboard-service-card__name">
                                                {item.productName}
                                            </span>
                                            <strong>{formatQuantity(item.quantity)}</strong>
                                            {/* O valor zerado também é informação: normalmente indica
                                                que o serviço veio de assinatura/Clube e não gerou
                                                recebimento direto. Exibimos todos os valores para que
                                                nenhum card pareça incompleto. */}
                                            <small>{formatCurrency(item.total)}</small>
                                        </div>
                                            ))}
                                        </div>
                                    </Fragment>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <section className={`employee-dashboard-extract${activeTab !== 'extrato' ? ' employee-dashboard-tab-hidden' : ''}`}>
                    <div className="employee-dashboard-extract__header">
                        <div>
                            <span className="employee-dashboard-overview__eyebrow">Histórico</span>
                            <h2>Extrato</h2>
                            <p>{formatRangeLabel(extractRange)}</p>
                        </div>
                        <div className="employee-dashboard-range-picker" ref={rangePickerRef}>
                            <button
                                type="button"
                                className="employee-dashboard-range-picker__trigger"
                                onClick={() => setRangePickerOpen((open) => !open)}
                                aria-haspopup="dialog"
                                aria-expanded={rangePickerOpen}
                                aria-label="Selecionar período do extrato"
                            >
                                <i className="fa-regular fa-calendar-days"></i>
                                {formatRangeLabel(extractRange)}
                                <i className={`fa-solid fa-chevron-${rangePickerOpen ? 'up' : 'down'}`}></i>
                            </button>
                            {rangePickerOpen && (
                                <div className="employee-dashboard-range-picker__menu" role="dialog" aria-label="Período do extrato">
                                    <label>
                                        De
                                        <input
                                            type="date"
                                            value={rangeDraft.start}
                                            max={rangeDraft.end || undefined}
                                            onChange={(event) => {
                                                setRangeDraft((draft) => ({ ...draft, start: event.target.value }));
                                                setRangeError(null);
                                            }}
                                        />
                                    </label>
                                    <label>
                                        Até
                                        <input
                                            type="date"
                                            value={rangeDraft.end}
                                            min={rangeDraft.start || undefined}
                                            onChange={(event) => {
                                                setRangeDraft((draft) => ({ ...draft, end: event.target.value }));
                                                setRangeError(null);
                                            }}
                                        />
                                    </label>
                                    <small className="employee-dashboard-range-picker__hint">
                                        Máximo de {MAX_EXTRACT_RANGE_DAYS} dias.
                                    </small>
                                    {rangeError && (
                                        <small className="employee-dashboard-range-picker__error">{rangeError}</small>
                                    )}
                                    <div className="employee-dashboard-range-picker__actions">
                                        <button type="button" onClick={resetExtractRange}>Este mês</button>
                                        <button type="button" className="primary" onClick={applyExtractRange}>Aplicar</button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="employee-dashboard-extract__switcher" role="tablist" aria-label="Tipo de extrato">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeLedger === 'faturamento'}
                                className={activeLedger === 'faturamento' ? 'active' : ''}
                                onClick={openRevenueHistory}
                            >
                                Faturamento
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeLedger === 'pontos'}
                                className={activeLedger === 'pontos' ? 'active' : ''}
                                onClick={openScoreHistory}
                            >
                                Pontos
                            </button>
                        </div>
                    </div>

                    {activeLedger === 'faturamento' && (
                        <div className="employee-dashboard-extract__body">
                            {revenueHistoryLoading && <div className="employee-dashboard-extract__loading"><Spinner animation="border" size="sm" /> Carregando faturamento...</div>}
                            {!revenueHistoryLoading && revenueHistoryError && <Alert variant="danger">{revenueHistoryError}</Alert>}
                            {!revenueHistoryLoading && !revenueHistoryError && revenueHistory !== null && revenueHistory.length === 0 && (
                                <div className="employee-dashboard-empty">
                                    <strong>Nenhum faturamento registrado neste período.</strong>
                                    <p>Os valores aparecem aqui conforme você realiza atendimentos.</p>
                                </div>
                            )}
                            {!revenueHistoryLoading && !revenueHistoryError && revenueHistory?.length > 0 && (
                                <>
                                    {/* As mesmas parcelas dos cards do resumo, e não um "receita
                                        do período" só: o extrato é aberto pelo card de Avulsos, e
                                        um total que somava serviço, produto e clube nunca batia
                                        com o card que levou o barbeiro até aqui. */}
                                    <div className="employee-dashboard-extract__total employee-dashboard-extract__totals">
                                        <div>
                                            <span>Avulsos</span>
                                            <strong>{formatCurrency(revenueLedgerTotals.avulsos)}</strong>
                                        </div>
                                        <div>
                                            <span>Produtos</span>
                                            <strong>{formatCurrency(revenueLedgerTotals.produtos)}</strong>
                                        </div>
                                        {/* Pote, e não "Clube": a soma do que saiu de graça pelo
                                            plano é sempre R$ 0,00, e uma caixa zerada não diz
                                            nada. O que ele quer ver dessa parte do trabalho é
                                            quanto ela rendeu, o mesmo valor do card lá em cima. */}
                                        <div>
                                            <span>Pote</span>
                                            <strong>{formatCurrency(dashboard?.subscriptionEarnings ?? 0)}</strong>
                                        </div>
                                        {/* Mesma regra do card: só existe para quem combinou. */}
                                        {Number(dashboard?.potBonus || 0) > 0 && (
                                            <div>
                                                <span>Adicional combinado</span>
                                                <strong>{formatCurrency(dashboard?.potBonus ?? 0)}</strong>
                                            </div>
                                        )}
                                        <div className="employee-dashboard-extract__commission-total">
                                            {/* A comissão é apurada por mês fechado: mesmo com um período livre na
                                                lista, este total continua sendo o do mês, e o rótulo diz isso para
                                                o colaborador não comparar recortes diferentes. */}
                                            <span>Comissão do mês</span>
                                            <strong>{formatCurrency(dashboard?.normalCommission || 0)}</strong>
                                        </div>
                                    </div>
                                    {/* Composição do avulso por categoria, logo abaixo dos totais:
                                        é a categoria que determina o percentual, então é aqui que
                                        ele confere se a comissão saiu como combinado. Some quando
                                        o período filtrado não tem avulso nenhum. */}
                                    {revenueLedgerByCategory.length > 0 && (
                                        <div className="employee-dashboard-extract__categories">
                                            <div className="employee-dashboard-extract__categories-header">
                                                <span>Avulsos por categoria</span>
                                                <span>Comissão</span>
                                            </div>
                                            {revenueLedgerByCategory.map(([categoryName, values]) => (
                                                <div key={categoryName} className="employee-dashboard-extract__categories-row">
                                                    <span className="employee-dashboard-extract__categories-name">{categoryName}</span>
                                                    <span className="employee-dashboard-extract__categories-revenue">{formatCurrency(values.revenue)}</span>
                                                    <strong className="employee-dashboard-extract__categories-commission">{formatCurrency(values.commission)}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="score-history-list">
                                        {revenueHistory.map((entry, index) => (
                                            <div key={index} className="score-history-item">
                                                <div className="score-history-item__left">
                                                    <span className="score-history-item__date">{entry.date ? new Date(entry.date).toLocaleDateString('pt-BR') : ''}</span>
                                                    <strong className="score-history-item__customer">{entry.customerName || 'Cliente não informado'}</strong>
                                                    <span className="score-history-item__service">{entry.serviceName}</span>
                                                </div>
                                                <div className="score-history-item__right">
                                                    <span className="score-history-item__pts">{formatCurrency(entry.total)}</span>
                                                    <small className="score-history-item__detail score-history-item__commission">Comissão: {formatCurrency(entry.commission)}</small>
                                                    {entry.clubMember && <small className="score-history-item__detail">Clube</small>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {activeLedger === 'pontos' && (
                        <div className="employee-dashboard-extract__body">
                            {scoreHistoryLoading && <div className="employee-dashboard-extract__loading"><Spinner animation="border" size="sm" /> Carregando pontos...</div>}
                            {!scoreHistoryLoading && scoreHistoryError && <Alert variant="danger">{scoreHistoryError}</Alert>}
                            {!scoreHistoryLoading && !scoreHistoryError && scoreHistory !== null && scoreHistory.length === 0 && (
                                <div className="employee-dashboard-empty">
                                    <strong>Nenhum ponto registrado neste período.</strong>
                                    <p>Os pontos são gerados quando você realiza atendimentos de assinatura.</p>
                                </div>
                            )}
                            {!scoreHistoryLoading && !scoreHistoryError && scoreHistory?.length > 0 && (
                                <>
                                    <div className="employee-dashboard-extract__total">
                                        <span>Total de pontos</span>
                                        <strong>{scoreHistory.reduce((sum, entry) => sum + entry.totalScore, 0)} pts</strong>
                                    </div>
                                    <div className="score-history-list">
                                        {scoreHistory.map((entry, index) => (
                                            <div key={index} className="score-history-item">
                                                <div className="score-history-item__left">
                                                    <span className="score-history-item__date">{entry.date ? new Date(entry.date).toLocaleDateString('pt-BR') : ''}</span>
                                                    <strong className="score-history-item__customer">{entry.customerName || 'Cliente não informado'}</strong>
                                                    <span className="score-history-item__service">{entry.serviceName}</span>
                                                </div>
                                                <div className="score-history-item__right">
                                                    <span className="score-history-item__pts">+{entry.totalScore} pts</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </section>

                <Card className={`booking-card shadow-lg employee-dashboard-card${activeTab !== 'agenda' ? ' employee-dashboard-tab-hidden' : ''}`}>
                    <Card.Body className="p-4">
                        <div className="employee-dashboard-section__header">
                            <div>
                                <h2 className="employee-dashboard-section__title">Agenda</h2>
                                <p className="employee-dashboard-section__subtitle">
                                    Próximos horários que entram na previsão deste mês.
                                </p>
                            </div>
                            {/* Só aparece quando a casa liberou o recurso: o servidor recusa de
                                qualquer forma, e um botão que sempre dá erro é pior que nenhum. */}
                            {dashboard?.employeeCanBlockAgenda === true && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="collab-portal-btn--primary"
                                    onClick={openBlockForm}
                                >
                                    <i className="fa-solid fa-lock me-2"></i>
                                    Bloquear horário
                                </Button>
                            )}
                        </div>

                        <div className="collab-weekly-toolbar">
                            <div>
                                <strong>
                                    {isMobileViewport
                                        ? calendarDate.toLocaleDateString('pt-BR', {
                                            weekday: 'long', day: '2-digit', month: 'long'
                                        }).replace(/^./, (letter) => letter.toUpperCase())
                                        : `${weekStart.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} a ${addDays(weekStart, 6).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                                </strong>
                                <small>{isMobileViewport ? 'Agenda de hoje' : 'Visualização semanal'}</small>
                            </div>
                            <div className="collab-weekly-toolbar__actions">
                                <Button
                                    variant="outline-secondary"
                                    className="collab-weekly-toolbar__nav"
                                    size="sm"
                                    onClick={() => moveCalendar(isMobileViewport ? -1 : -7)}
                                    aria-label={isMobileViewport ? 'Dia anterior' : 'Semana anterior'}
                                >
                                    <i className="fa-solid fa-chevron-left"></i>
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    className="collab-weekly-toolbar__current"
                                    size="sm"
                                    onClick={goToToday}
                                >
                                    {isMobileViewport ? 'Hoje' : 'Esta semana'}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    className="collab-weekly-toolbar__nav"
                                    size="sm"
                                    onClick={() => moveCalendar(isMobileViewport ? 1 : 7)}
                                    aria-label={isMobileViewport ? 'Próximo dia' : 'Próxima semana'}
                                >
                                    <i className="fa-solid fa-chevron-right"></i>
                                </Button>
                            </div>
                        </div>

                        {weeklyAgendaError && <Alert variant="danger" className="py-2">{weeklyAgendaError}</Alert>}
                        {weeklyAgendaLoading && (
                            <div className="collab-weekly-loading"><Spinner animation="border" size="sm" /> Carregando semana...</div>
                        )}
                        {!weeklyAgendaLoading && !weeklyAgendaError && (
                            <div className="collab-weekly-calendar">
                                <Calendar
                                    localizer={portalCalendarLocalizer}
                                    date={calendarDate}
                                    view={isMobileViewport ? 'day' : 'week'}
                                    events={weeklyCalendarEvents}
                                    startAccessor="start"
                                    endAccessor="end"
                                    min={weeklyCalendarBounds.min}
                                    max={weeklyCalendarBounds.max}
                                    scrollToTime={weeklyCalendarBounds.min}
                                    step={10}
                                    timeslots={3}
                                    toolbar={false}
                                    popup
                                    // Clique num horário vazio abre o agendamento rápido. No toque,
                                    // o calendário exige segurar o dedo; o limite curto deixa o
                                    // toque normal funcionar sem atrapalhar a rolagem.
                                    selectable={canSchedule}
                                    longPressThreshold={120}
                                    onSelectSlot={handleCalendarSlotSelect}
                                    onNavigate={(date) => {
                                        setCalendarDate(date);
                                        setWeekStart(getWeekStart(date));
                                    }}
                                    onSelectEvent={(event) => {
                                        // Bloqueio e atendimento dividem a grade, mas não a ação:
                                        // um abre o detalhe do cliente, o outro a reabertura do
                                        // horário.
                                        if (event.resource?.agendaBlock) {
                                            setSelectedBlock(event.resource.agendaBlock);
                                            return;
                                        }
                                        setSelectedAppointment(event.resource);
                                    }}
                                    dayPropGetter={getCalendarDayStyle}
                                    eventPropGetter={getPortalCalendarEventStyle}
                                    components={{ event: PortalCalendarEvent }}
                                    formats={{ dayFormat: formatPortalCalendarDay }}
                                    messages={{
                                        noEventsInRange: 'Nenhum atendimento nesta semana'
                                    }}
                                />
                            </div>
                        )}

                        {false && !groupedAppointments?.length && (
                            <div className="employee-dashboard-empty">
                                <strong>Nenhum horário futuro encontrado.</strong>
                                <p>A agenda pública deste colaborador ainda não tem atendimentos previstos para o restante do mês.</p>
                            </div>
                        )}

                        {!!groupedAppointments?.length && (
                            <div className="collab-agenda collab-agenda--legacy">
                                {groupedAppointments.map((group) => {
                                    const groupLabel = formatGroupHeader(group.dateKey);
                                    // 'Hoje'/'Amanhã' são rótulos relativos; nesses casos mostramos a data curta no meta.
                                    const isRelative = groupLabel === 'Hoje' || groupLabel === 'Amanhã';
                                    const isToday = groupLabel === 'Hoje';
                                    const shortDate = new Intl.DateTimeFormat('pt-BR', {
                                        weekday: 'short', day: '2-digit', month: 'short'
                                    }).format(new Date(group.dateKey + 'T12:00:00'))
                                        .replace(/\./g, '')
                                        .replace(/^./, (letter) => letter.toUpperCase());
                                    const count = group.items.length;
                                    const countLabel = `${count} ${count === 1 ? 'atendimento' : 'atendimentos'}`;

                                    return (
                                        <div key={group.dateKey} className="collab-agenda-day">
                                            <div className={`collab-agenda-day-header${isToday ? ' collab-agenda-day-header--today' : ''}`}>
                                                <span className="collab-agenda-day-title">{groupLabel}</span>
                                                <span className="collab-agenda-day-meta">{isRelative ? `${shortDate} · ${countLabel}` : countLabel}</span>
                                            </div>

                                            <div className="collab-agenda-list">
                                                {buildDayTimeline(group.items, group.dateKey, dashboard?.workingHours).map((entry) => {
                                                    if (entry.kind === 'free') {
                                                        const durMin = Math.round((entry.end - entry.start) / 60000);
                                                        return (
                                                            <div key={`free-${entry.start.getTime()}`} className="collab-agenda-gap">
                                                                <span className="collab-agenda-gap-line"></span>
                                                                <span className="collab-agenda-gap-label">
                                                                    <i className="fa-regular fa-circle-check me-1"></i>
                                                                    Livre · {formatHourMinute(entry.start)} → {formatHourMinute(entry.end)}
                                                                    <span className="collab-agenda-gap-dur">({formatGapDuration(durMin)})</span>
                                                                </span>
                                                                <span className="collab-agenda-gap-line"></span>
                                                            </div>
                                                        );
                                                    }

                                                    if (entry.kind === 'break') {
                                                        return (
                                                            <div key={`break-${entry.start.getTime()}`} className="collab-agenda-gap collab-agenda-gap--break">
                                                                <span className="collab-agenda-gap-line"></span>
                                                                <span className="collab-agenda-gap-label">
                                                                    <i className="fa-solid fa-mug-hot me-1"></i>
                                                                    Intervalo · {formatHourMinute(entry.start)} → {formatHourMinute(entry.end)}
                                                                </span>
                                                                <span className="collab-agenda-gap-line"></span>
                                                            </div>
                                                        );
                                                    }

                                                    const appointment = entry.appointment;
                                                    const timeStr = formatHourMinute(entry.start);
                                                    const durationStr = appointment.duration ? `${appointment.duration} min` : '30 min';
                                                    const variant = getStatusVariant(appointment.status);

                                                    return (
                                                        <div
                                                            key={appointment.appointmentUuid}
                                                            className={`collab-agenda-card collab-agenda-card--${variant}`}
                                                            onClick={() => setSelectedAppointment(appointment)}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => e.key === 'Enter' && setSelectedAppointment(appointment)}
                                                        >
                                                            <div className="collab-agenda-time">
                                                                <span className="collab-agenda-time-hour">{timeStr}</span>
                                                                <span className="collab-agenda-time-dur">
                                                                    <i className="fa-regular fa-clock"></i> {durationStr}
                                                                </span>
                                                            </div>

                                                            <div className="collab-agenda-info">
                                                                <div className="collab-agenda-client-row">
                                                                    <span className="collab-agenda-client">
                                                                        {appointment.customerName || 'Cliente não informado'}
                                                                    </span>
                                                                    {appointment.clubMember && (
                                                                        <span className="collab-agenda-club-badge" title="Cliente do clube">
                                                                            <i className="fa-solid fa-crown"></i>
                                                                            Clube
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="collab-agenda-service">
                                                                    {formatServices(appointment)}
                                                                </span>
                                                                <span className="collab-agenda-branch">
                                                                    <i className="fa-solid fa-location-dot me-1"></i>
                                                                    {appointment.branchName || 'Sem filial'}
                                                                </span>
                                                            </div>

                                                            <div className="collab-agenda-right">
                                                                {/* Na própria agenda, "Agendado" é o estado implícito do card.
                                                                    Mantemos badges apenas quando comunicam uma situação que exige
                                                                    leitura adicional, como confirmado, concluído ou cancelado. */}
                                                                {appointment.status !== 'SCHEDULED' && (
                                                                    <Badge bg={variant} pill className="collab-agenda-badge">
                                                                        {getStatusLabel(appointment.status)}
                                                                    </Badge>
                                                                )}
                                                                <i className="fa-solid fa-chevron-right collab-agenda-chevron"></i>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Container>

            <Modal
                show={!!selectedBlock}
                onHide={() => setSelectedBlock(null)}
                centered
                dialogClassName="employee-appointment-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Horário bloqueado</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {blockError && <Alert variant="danger" className="py-2">{blockError}</Alert>}
                    <div className="collab-block-details">
                        <div>
                            <span>Quando</span>
                            <strong>
                                {selectedBlock && new Date(selectedBlock.startDate).toLocaleDateString('pt-BR', {
                                    weekday: 'long', day: '2-digit', month: 'long'
                                }).replace(/^./, (letter) => letter.toUpperCase())}
                            </strong>
                        </div>
                        <div>
                            <span>Horário</span>
                            <strong>
                                {selectedBlock && `${formatHourMinute(new Date(selectedBlock.startDate))} - ${formatHourMinute(new Date(selectedBlock.endDate))}`}
                            </strong>
                        </div>
                        <div>
                            <span>Motivo</span>
                            <strong>{selectedBlock?.reason || 'Não informado'}</strong>
                        </div>
                    </div>
                    <p className="collab-block-form__hint mt-3">
                        Ao reabrir, o horário volta a aceitar agendamentos.
                    </p>
                </Modal.Body>
                <Modal.Footer className="employee-appointment-modal-footer">
                    <Button
                        variant="secondary"
                        onClick={() => setSelectedBlock(null)}
                        className="employee-appointment-modal-close-btn px-4"
                    >
                        Fechar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => removeAgendaBlock(selectedBlock)}
                        disabled={blockSaving}
                        className="collab-portal-btn--primary px-4"
                    >
                        {blockSaving ? 'Reabrindo...' : 'Reabrir horário'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={blockFormOpen}
                onHide={() => setBlockFormOpen(false)}
                centered
                dialogClassName="employee-appointment-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Bloquear horário</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="collab-block-form__hint">
                        O horário fica indisponível para novos agendamentos. Quem já tem hora marcada
                        continua marcado: para abrir espaço, cancele o atendimento antes.
                    </p>
                    {blockError && <Alert variant="danger" className="py-2">{blockError}</Alert>}
                    <div className="collab-block-form">
                        <label>
                            <span>Dia</span>
                            <input
                                type="date"
                                /* Dia passado é recusado pelo servidor; barrar aqui evita que ele
                                   preencha o formulário inteiro para receber o erro no fim. */
                                min={formatDateKey(new Date())}
                                value={blockForm?.date || ''}
                                onChange={(event) => setBlockForm({ ...blockForm, date: event.target.value })}
                            />
                        </label>
                        <div className="collab-block-form__row">
                            <label>
                                <span>Das</span>
                                <input
                                    type="time"
                                    value={blockForm?.startTime || ''}
                                    onChange={(event) => setBlockForm({ ...blockForm, startTime: event.target.value })}
                                />
                            </label>
                            <label>
                                <span>Até</span>
                                <input
                                    type="time"
                                    value={blockForm?.endTime || ''}
                                    onChange={(event) => setBlockForm({ ...blockForm, endTime: event.target.value })}
                                />
                            </label>
                        </div>
                        {/* A unidade só é perguntada a quem atende em mais de uma; para os demais o
                            servidor resolve sozinho. */}
                        {(dashboard?.employee?.branches?.length || 0) > 1 && (
                            <label>
                                <span>Unidade</span>
                                <select
                                    value={blockForm?.branchId || ''}
                                    onChange={(event) => setBlockForm({ ...blockForm, branchId: event.target.value })}
                                >
                                    <option value="">Escolha a unidade</option>
                                    {dashboard.employee.branches.map((branch) => (
                                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <label>
                            <span>Motivo (opcional)</span>
                            <input
                                type="text"
                                maxLength={120}
                                placeholder="Ex.: consulta médica"
                                value={blockForm?.reason || ''}
                                onChange={(event) => setBlockForm({ ...blockForm, reason: event.target.value })}
                            />
                        </label>
                    </div>
                </Modal.Body>
                <Modal.Footer className="employee-appointment-modal-footer">
                    <Button
                        variant="secondary"
                        onClick={() => setBlockFormOpen(false)}
                        className="employee-appointment-modal-close-btn px-4"
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={submitBlockForm}
                        disabled={blockSaving}
                        className="collab-portal-btn--primary px-4"
                    >
                        {blockSaving ? 'Bloqueando...' : 'Bloquear'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={!!selectedServiceDetails}
                onHide={() => setSelectedServiceDetails(null)}
                centered
                dialogClassName="employee-appointment-modal"
            >
                <Modal.Header closeButton>
                    {/* O canal no título: o mesmo serviço tem um card em Avulsos e outro em
                        Clube, e sem isso as duas janelas chegavam com o mesmo nome e números
                        diferentes. Produto não se divide por canal e por isso não ganha selo. */}
                    <Modal.Title>
                        {selectedServiceDetails?.productName}
                        {selectedServiceDetails?.channel !== 'all' && (
                            <small className="employee-service-details-channel">
                                {selectedServiceDetails?.channel === 'club' ? 'Clube' : 'Avulso'}
                            </small>
                        )}
                    </Modal.Title>
                    {serviceDetails.length > 0 && (
                        <PdfExportButton
                            contentRef={serviceDetailsPrintRef}
                            documentTitle={`${selectedServiceDetails?.productName || 'servico'}-${selectedMonth}`}
                            className="d-print-none ms-auto me-2 employee-service-details-pdf"
                            variant="outline-secondary"
                        >
                            <PdfIcon className="me-2" />
                            PDF
                        </PdfExportButton>
                    )}
                </Modal.Header>
                <Modal.Body>
                    {serviceDetailsLoading && <div className="employee-dashboard-extract__loading"><Spinner animation="border" size="sm" /> Carregando atendimentos...</div>}
                    {!serviceDetailsLoading && serviceDetails.length === 0 && (
                        <div className="employee-dashboard-empty">
                            <strong>Nenhum atendimento detalhado encontrado.</strong>
                            <p>Os lançamentos deste serviço aparecerão aqui quando forem registrados.</p>
                        </div>
                    )}
                    {!serviceDetailsLoading && serviceDetails.length > 0 && (
                        <div ref={serviceDetailsPrintRef} className="employee-service-details-printable">
                            {/* Só aparece no PDF: fora da tela o título já está no modal, mas no
                                papel a folha chegaria sem dizer de quem é nem de que mês. */}
                            <div className="employee-service-details-print-header">
                                <strong>
                                    {selectedServiceDetails?.productName}
                                    {selectedServiceDetails?.channel !== 'all'
                                        && ` · ${selectedServiceDetails?.channel === 'club' ? 'Clube' : 'Avulso'}`}
                                </strong>
                                <span>{dashboard?.employeeName} · {formatMonthReference(selectedMonth)}</span>
                            </div>
                            <div className="score-history-summary">
                                <div className="score-history-summary__total">
                                    <span className="score-history-summary__label">Total realizado</span>
                                    <span className="score-history-summary__value">
                                        {serviceDetails.reduce((sum, entry) => sum + Number(entry.qty || 0), 0)}
                                    </span>
                                </div>
                                {/* A lista é de um canal só, então não há mais o que quebrar
                                    entre avulso e clube: sobra o que ele veio ver, quanto
                                    rendeu e quanto disso é dele. */}
                                <div className="score-history-summary__breakdown" aria-label="Resumo dos atendimentos">
                                    <span>
                                        <strong>{formatCurrency(serviceDetails.reduce((sum, entry) => sum + Number(entry.total || 0), 0))}</strong>
                                        <small>Receita</small>
                                    </span>
                                    <span>
                                        <strong>{formatCurrency(serviceDetails.reduce((sum, entry) => sum + Number(entry.commission || 0), 0))}</strong>
                                        <small>Comissão</small>
                                    </span>
                                </div>
                            </div>
                            {/* O barbeiro não precisa da taxonomia de plano; precisa saber sobre o
                                que a comissão dele incide. Uma linha basta, e o R$ 0,00 na própria
                                linha já explica o atendimento que não entrou. */}
                            <p className="employee-service-details-legend">
                                Sua comissão é calculada sobre a receita.
                            </p>
                            <div className="score-history-list">
                                {serviceDetails.map((entry, index) => (
                                    <div key={`${entry.date}-${index}`} className="score-history-item">
                                        <div className="score-history-item__left">
                                            <span className="score-history-item__date">{entry.date ? new Date(entry.date).toLocaleDateString('pt-BR') : ''}</span>
                                            <strong className="score-history-item__customer">{entry.customerName || 'Cliente não informado'}</strong>
                                            {Number(entry.qty) > 1 && (
                                                <span className="score-history-item__service">{entry.qty}× realizado</span>
                                            )}
                                        </div>
                                        <div className="score-history-item__right">
                                            <span className="score-history-item__pts">{formatCurrency(entry.total)}</span>
                                            <small className="score-history-item__detail score-history-item__commission">Comissão: {formatCurrency(entry.commission)}</small>
                                            {entry.clubMember && (
                                                <small className="score-history-item__detail">Clube</small>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="employee-appointment-modal-footer">
                    <Button variant="secondary" onClick={() => setSelectedServiceDetails(null)} className="employee-appointment-modal-close-btn px-4">
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Appointment Details Modal */}
            <Modal 
                show={!!selectedAppointment} 
                onHide={() => setSelectedAppointment(null)} 
                centered
                dialogClassName="employee-appointment-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Detalhes do Atendimento</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAppointment && (() => {
                        const customerFirstName = selectedAppointment.customerName ? selectedAppointment.customerName.split(' ')[0] : 'Cliente';
                        const appointmentDateStr = selectedAppointment.scheduledDate ? new Date(selectedAppointment.scheduledDate).toLocaleDateString('pt-BR') : '';
                        const appointmentTimeStr = selectedAppointment.scheduledDate ? new Date(selectedAppointment.scheduledDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
                        
                        const companyName = companyInfo?.name || '';
                        const employeeName = dashboard?.employeeName || '';
                        const serviceName = formatServices(selectedAppointment);
                        
                        const waMessage = `Olá, ${customerFirstName}! Tudo bem? Aqui é o(a) ${employeeName} da ${companyName}. Estou entrando em contato para confirmar nosso agendamento de ${serviceName} no dia ${appointmentDateStr} às ${appointmentTimeStr}.`;
                        const waUrl = buildWhatsappUrl(selectedAppointment.customerPhone, waMessage);
                        const telUrl = `tel:${selectedAppointment.customerPhone}`;
                        
                        return (
                            <div className="employee-appointment-detail-container">
                                <div className="employee-appointment-card-header">
                                    <div className="employee-appointment-date-info">
                                        <span className="employee-appointment-date-main">
                                            <i className="fa-solid fa-calendar-day me-2"></i>
                                            {formatDateOnly(selectedAppointment.scheduledDate)}
                                        </span>
                                        <span className="employee-appointment-time-sub">
                                            <i className="fa-solid fa-clock me-2"></i>
                                            {formatTimeRange(selectedAppointment.scheduledDate, selectedAppointment.endTime)} ({selectedAppointment.duration || 30} min)
                                        </span>
                                    </div>
                                    <Badge bg={getStatusVariant(selectedAppointment.status)} pill className="px-3 py-2">
                                        {getStatusLabel(selectedAppointment.status)}
                                    </Badge>
                                </div>

                                <div className="employee-appointment-grid">
                                    {/* Cliente Box */}
                                    <div className="employee-appointment-section-box">
                                        <span className="employee-appointment-section-title">
                                            <i className="fa-solid fa-user me-1"></i> Cliente
                                        </span>
                                        <div className="employee-appointment-customer-profile">
                                            <div className="employee-appointment-customer-details">
                                                <div className="employee-appointment-customer-name-row">
                                                    <span className="employee-appointment-customer-name" title={selectedAppointment.customerName}>
                                                        {selectedAppointment.customerName || 'Cliente não informado'}
                                                    </span>
                                                    {selectedAppointment.clubMember && (
                                                        <span className="collab-agenda-club-badge" title="Cliente do clube">
                                                            <i className="fa-solid fa-crown"></i>
                                                            Clube
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {selectedAppointment.customerPhone && (
                                            <div className="employee-appointment-actions">
                                                <a href={telUrl} className="employee-appointment-btn-action">
                                                    <i className="fa-solid fa-phone me-1"></i> Ligar
                                                </a>
                                                <a href={waUrl} target="_blank" rel="noopener noreferrer" className="employee-appointment-btn-action employee-appointment-btn-action--whatsapp">
                                                    <i className="fa-brands fa-whatsapp me-1"></i> WhatsApp
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    {/* Serviço Box */}
                                    <div className="employee-appointment-section-box employee-appointment-section-box--accent">
                                        <span className="employee-appointment-section-title">
                                            <i className="fa-solid fa-clipboard-list me-1"></i> Serviço & Faturamento
                                        </span>
                                        <div className="d-flex flex-column h-100 justify-content-between">
                                            <span className="employee-appointment-service-name" title={formatServices(selectedAppointment)}>
                                                {formatServices(selectedAppointment)}
                                            </span>
                                            <div className="employee-appointment-service-meta">
                                                <span className="employee-appointment-service-duration">
                                                    <i className="fa-regular fa-clock me-1"></i> {selectedAppointment.duration || 30} min
                                                </span>
                                                <div className="text-end">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 600 }}>Valor Estimado</small>
                                                    <strong className="employee-appointment-service-revenue">
                                                        {formatCurrency(selectedAppointment.estimatedRevenue)}
                                                    </strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Filial Box */}
                                    <div className="employee-appointment-section-box" style={{ gridColumn: '1 / -1' }}>
                                        <span className="employee-appointment-section-title">
                                            <i className="fa-solid fa-location-dot me-1"></i> Filial / Local
                                        </span>
                                        <div className="d-flex align-items-center gap-2">
                                            <strong className="employee-appointment-branch-name" style={{ fontSize: '0.92rem' }}>
                                                {selectedAppointment.branchName || 'Sem filial definida'}
                                            </strong>
                                        </div>
                                    </div>

                                    {/* Observações Box */}
                                    {selectedAppointment.notes && (
                                        <div className="employee-appointment-notes-box">
                                            <span className="employee-appointment-section-title">
                                                <i className="fa-solid fa-comment-dots me-1"></i> Observações do Cliente
                                            </span>
                                            <p className="employee-appointment-notes-content mt-1">
                                                "{selectedAppointment.notes}"
                                            </p>
                                        </div>
                                    )}

                                    {/* Sem cliente no atendimento não há de quem mostrar visitas. */}
                                    {selectedAppointment.customerName && selectedAppointment.appointmentUuid && (
                                        <PortalCustomerHistory
                                            portalToken={portalToken}
                                            appointmentUuid={selectedAppointment.appointmentUuid}
                                            onSessionExpired={() => {
                                                localStorage.removeItem(storageKey);
                                                navigate(`/p/${slug}/portal/login`, { replace: true });
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer className="employee-appointment-modal-footer">
                    <Button variant="secondary" onClick={() => setSelectedAppointment(null)} className="employee-appointment-modal-close-btn px-4">
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Score History Modal */}
            <Modal
                show={showScoreHistory}
                onHide={() => setShowScoreHistory(false)}
                centered
                dialogClassName="employee-appointment-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Extrato de Pontos: {dashboard?.monthReference ? (() => {
                        const [y, m] = dashboard.monthReference.split('-').map(Number);
                        const d = new Date(y, m - 1, 1);
                        const label = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(d);
                        return label.charAt(0).toUpperCase() + label.slice(1);
                    })() : ''}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">
                        <strong>Clube:</strong> atendimentos de clientes com assinatura.
                    </p>
                    {scoreHistoryLoading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                            <p className="mt-2 mb-0 text-muted" style={{ fontSize: '0.875rem' }}>Carregando extrato…</p>
                        </div>
                    )}
                    {!scoreHistoryLoading && scoreHistoryError && (
                        <div className="collab-login-error" role="alert" style={{ borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: 0 }}>
                            <i className="fa-solid fa-circle-exclamation me-2"></i>
                            {scoreHistoryError}
                        </div>
                    )}
                    {!scoreHistoryLoading && !scoreHistoryError && scoreHistory !== null && scoreHistory.length === 0 && (
                        <div className="employee-dashboard-empty">
                            <strong>Nenhum ponto registrado este mês.</strong>
                            <p>Os pontos são gerados quando você realiza atendimentos de assinatura.</p>
                        </div>
                    )}
                    {!scoreHistoryLoading && !scoreHistoryError && scoreHistory !== null && scoreHistory.length > 0 && (
                        <>
                            <div className="score-history-summary">
                                <span className="score-history-summary__label">
                                    Total acumulado no mês
                                </span>
                                <span className="score-history-summary__value">
                                    {scoreHistory.reduce((sum, e) => sum + e.totalScore, 0)} pts
                                </span>
                            </div>
                            <div className="score-history-list">
                                {scoreHistory.map((entry, idx) => {
                                    const dateStr = entry.date
                                        ? new Date(entry.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                                        : '';
                                    return (
                                        <div key={idx} className="score-history-item">
                                            <div className="score-history-item__left">
                                                <span className="score-history-item__date">{dateStr}</span>
                                                <strong className="score-history-item__customer">
                                                    {entry.customerName || 'Cliente não informado'}
                                                </strong>
                                                <span className="score-history-item__service">{entry.serviceName}</span>
                                            </div>
                                            <div className="score-history-item__right">
                                                <span className="score-history-item__pts">+{entry.totalScore} pts</span>
                                                {entry.qty > 1 && (
                                                    <small className="score-history-item__detail">
                                                        {entry.qty}× {entry.scorePerUnit} pts
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="employee-appointment-modal-footer">
                    <Button variant="secondary" onClick={() => setShowScoreHistory(false)} className="employee-appointment-modal-close-btn px-4">
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Revenue History Modal */}
            <Modal
                show={showRevenueHistory}
                onHide={() => setShowRevenueHistory(false)}
                centered
                dialogClassName="employee-appointment-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Faturamento: {formatMonthReference(dashboard?.monthReference)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {revenueHistoryLoading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                            <p className="mt-2 mb-0 text-muted" style={{ fontSize: '0.875rem' }}>Carregando extrato…</p>
                        </div>
                    )}
                    {!revenueHistoryLoading && revenueHistoryError && (
                        <div className="collab-login-error" role="alert" style={{ borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: 0 }}>
                            <i className="fa-solid fa-circle-exclamation me-2"></i>
                            {revenueHistoryError}
                        </div>
                    )}
                    {!revenueHistoryLoading && !revenueHistoryError && revenueHistory !== null && revenueHistory.length === 0 && (
                        <div className="employee-dashboard-empty">
                            <strong>Nenhum faturamento registrado este mês.</strong>
                            <p>Os valores aparecem aqui conforme você realiza atendimentos.</p>
                        </div>
                    )}
                    {!revenueHistoryLoading && !revenueHistoryError && revenueHistory !== null && revenueHistory.length > 0 && (
                        <>
                            <div className="score-history-summary">
                                <div className="score-history-summary__total">
                                    <span className="score-history-summary__label">
                                        Receita do mês
                                    </span>
                                    <span className="score-history-summary__value">
                                        {formatCurrency(revenueHistory.reduce((sum, e) => sum + e.total, 0))}
                                    </span>
                                </div>
                                <div className="score-history-summary__total score-history-summary__commission">
                                    <span className="score-history-summary__label">
                                        Comissão do mês
                                    </span>
                                    <span className="score-history-summary__value">
                                        {formatCurrency(dashboard?.normalCommission || 0)}
                                    </span>
                                </div>
                                <div className="score-history-summary__breakdown" aria-label="Distribuição dos atendimentos">
                                    <span>
                                        <strong>{revenueHistory.length}</strong>
                                        <small>Total</small>
                                    </span>
                                    <span>
                                        <strong>{revenueHistory.filter((entry) => entry.clubMember).length}</strong>
                                        <small>Clube</small>
                                    </span>
                                    <span>
                                        <strong>{revenueHistory.filter((entry) => !entry.clubMember).length}</strong>
                                        <small>Avulsos</small>
                                    </span>
                                </div>
                            </div>
                            <p className="employee-dashboard-commission-note">
                                A comissão considera as regras vigentes, descontos aplicados e exclui atendimentos gratuitos do Clube, que entram na pontuação do pote.
                            </p>
                            <div className="score-history-list">
                                {revenueHistory.map((entry, idx) => {
                                    const dateStr = entry.date
                                        ? new Date(entry.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                                        : '';
                                    return (
                                        <div key={idx} className="score-history-item">
                                            <div className="score-history-item__left">
                                                <span className="score-history-item__date">{dateStr}</span>
                                                <div className="collab-agenda-client-row">
                                                    <span className="collab-agenda-client">
                                                        {entry.customerName || 'Cliente não informado'}
                                                    </span>
                                                    {entry.clubMember && (
                                                        <ClubCrownIcon
                                                            className="collab-agenda-club-badge"
                                                            title="Cliente do clube"
                                                            aria-label="Cliente do clube"
                                                        />
                                                    )}
                                                </div>
                                                <span className="score-history-item__service">{entry.serviceName}</span>
                                            </div>
                                            <div className="score-history-item__right">
                                                <span className="score-history-item__pts">
                                                    {formatCurrency(entry.total)}
                                                    <small className="score-history-item__detail score-history-item__commission">Comissão: {formatCurrency(entry.commission)}</small>
                                                </span>
                                                {entry.qty > 1 && (
                                                    <small className="score-history-item__detail">
                                                        {entry.qty}× {formatCurrency(entry.pricePerUnit)}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="employee-appointment-modal-footer">
                    <Button variant="secondary" onClick={() => setShowRevenueHistory(false)} className="employee-appointment-modal-close-btn px-4">
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Atalho para agendar sem procurar o horário na grade. Só na aba Agenda, que é
                onde o resultado aparece. */}
            {canSchedule && activeTab === 'agenda' && !quickBookingOpen && (
                <button
                    type="button"
                    className="collab-quick-booking-fab"
                    onClick={() => openQuickBooking()}
                    aria-label="Novo agendamento"
                    title="Novo agendamento"
                >
                    <i className="fa-solid fa-plus"></i>
                </button>
            )}

            <CollaboratorQuickBooking
                open={quickBookingOpen}
                initialStart={quickBookingStart}
                portalToken={portalToken}
                companySlug={slug}
                employee={dashboard?.employee}
                onClose={() => setQuickBookingOpen(false)}
                onCreated={handleQuickBookingCreated}
            />

        </div>
    );
};

export default PublicEmployeeDashboardPage;
